/**
 * COMPREHENSIVE AFFILIATE MANAGER
 *
 * Complete affiliate management system with:
 * - Enhanced USDT commission withdrawal system with KYC validation
 * - Real-time commission to shares conversion with atomic operations
 * - Username-based share transfer system with validation
 * - Comprehensive transaction history and audit trails
 * - Email notifications for all financial operations
 * - Security measures and rate limiting
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { getSessionHeaders } from '../../lib/serverSession'
import { affiliateTransactionService } from '../../lib/services/affiliateTransactionService';
import { LockedWalletManager, NETWORK_TO_LOCKED_KEY } from '../user/LockedWalletManager';
import type { LockedWallet } from '../user/LockedWalletManager';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '';
import { validateKYCForFinancialOperations, KYCValidationResult } from '../../lib/kycValidation';
import { DIVIDENDS_USER_VISIBLE } from '../../lib/dividendsUserVisible';
import { realtimeValidation } from '../../lib/realtimeValidation';
import { NotificationCenter } from '../user/NotificationCenter';
import { calculateUserShares, ShareBreakdown } from '../../lib/services/shareCalculationService';
import { WithdrawalEmailVerificationModal, WithdrawalConfirmationData } from '../withdrawals/WithdrawalEmailVerificationModal';
import { normalizeWithdrawalNetwork } from '../../lib/networkNormalization';
import { floorToCents, formatFlooredUsd } from '../../lib/withdrawalMoney';
import { isProductionCapitalDateActive } from '../../lib/productionCapitalCampaign.js';
import { CommissionPackageChooser, CommissionPackageCart } from './CommissionPackageChooser';

/**
 * Derive the number of shares credited for a commission→shares conversion.
 * Older approved conversions were stored with shares_requested = 0 because the
 * shares were materialised into consolidated purchase rows instead of being
 * written back onto the conversion record — which made the UI show "+0 Shares"
 * even though the shares were actually credited. Fall back to
 * usdt_amount / share_price so the displayed count matches what was credited.
 */
function getConversionShares(conversion: any): number {
  const recorded = parseFloat(conversion?.shares_requested ?? 0);
  if (recorded && recorded > 0) return recorded;
  const usdt = parseFloat(conversion?.usdt_amount ?? 0);
  const price = parseFloat(conversion?.share_price ?? 0);
  if (usdt > 0 && price > 0) return usdt / price;
  return 0;
}

interface TopupCompanyWallet {
  id: string;
  network: string;
  currency: string;
  wallet_address: string;
  name: string;
  technical: string;
  icon?: string;
}

interface PendingTopup {
  id: string;
  topup_payment_amount: string | number;
  transaction_reference: string | null;
  status: string;
  created_at: string;
}

interface ComprehensiveAffiliateManagerProps {
  userId: number;
  onDataRefresh?: () => void;
  onNavigateToSupport?: () => void;
}

// Pagination state interface
interface PaginationState {
  usdtTransactions: number;
  shareTransactions: number;
  conversions: number;
  withdrawals: number;
}

interface AffiliateData {
  user: {
    id: number;
    username: string;
    email: string;
    full_name?: string;
  };
  referralCount: number;
  commissionBalance: {
    usdt_balance: number;
    escrowed_amount?: number;
    share_balance: number;
    total_earned_usdt: number;
    total_earned_shares: number;
    total_withdrawn: number;
    displayed_balance?: number;
    computed_withdrawable_balance?: number;
    withdrawal_request_max_usdt?: number;
    source_of_funds_ceiling?: number;
    network_fee?: number;
    pending_withdrawal_count?: number;
    withdrawal_blocked?: boolean;
    withdrawal_block_reason_code?: string | null;
    withdrawal_block_reason_message?: string | null;
    // Add conversion tracking
    total_converted_shares?: number;
    total_usdt_converted?: number;
    total_shares_owned?: number;
    // Add purchased shares tracking
    purchased_shares?: number;
    purchased_amount?: number;
    // Add transfer tracking
    shares_sent?: number;
    shares_received?: number;
    net_transferred_shares?: number;
  };
  referralStats: {
    total_referrals: number;
    active_referrals: number;
    total_commission_earned: number;
  };
  recentTransactions: any[];
  withdrawalHistory: any[];
  conversionHistory: any[];
  transferHistory: any[];
  purchaseHistory: any[];
  currentPhase: {
    id: number;
    phase_name: string;
    price_per_share: string;
  };
}

interface WithdrawalFormData {
  amount: string;
  walletAddress: string;
  network: string;
}

interface ConversionFormData {
  usdtAmount: string;
}

interface TransferFormData {
  recipientUsername: string;
  shareAmount: string;
}

export const ComprehensiveAffiliateManager: React.FC<ComprehensiveAffiliateManagerProps> = ({
  userId,
  onDataRefresh,
  onNavigateToSupport
}) => {
  const [loading, setLoading] = useState(true);
  const [affiliateData, setAffiliateData] = useState<AffiliateData | null>(null);
  const [activeTab, setActiveTab] = useState<'overview' | 'transactions' | 'convert' | 'transfer' | 'withdraw' | 'history' | 'notifications' | 'statement'>('overview');
  const [activeTransactionTab, setActiveTransactionTab] = useState<'usdt' | 'shares' | 'purchased' | 'conversions' | 'withdrawals'>('usdt');
  const [error, setError] = useState<string | null>(null);

  // KYC validation state
  const [kycValidation, setKycValidation] = useState<KYCValidationResult | null>(null);

  // Locked wallet state
  const [lockedWallets, setLockedWallets] = useState<LockedWallet[]>([]);
  const [walletLocked, setWalletLocked] = useState(false);

  const rawUsdtBalance = Math.max(
    0,
    Number(affiliateData?.commissionBalance.displayed_balance ?? affiliateData?.commissionBalance.usdt_balance ?? 0) || 0
  );
  const withdrawalNetworkFee = Number(affiliateData?.commissionBalance.network_fee ?? 2) || 2;
  const maxWithdrawalAmount = floorToCents(Math.max(
    0,
    Number(affiliateData?.commissionBalance.withdrawal_request_max_usdt ?? Math.max(0, rawUsdtBalance - withdrawalNetworkFee)) || 0
  ));
  const clientDisplayBalance = rawUsdtBalance;
  const sourceOfFundsRoom = Math.max(
    0,
    Number(affiliateData?.commissionBalance.source_of_funds_ceiling
      ?? affiliateData?.commissionBalance.computed_withdrawable_balance
      ?? maxWithdrawalAmount) || 0
  );
  const escrowedWithdrawalAmount = Math.max(0, Number(affiliateData?.commissionBalance.escrowed_amount ?? 0) || 0);
  const convertedToSharesAmount = Math.max(0, Number(affiliateData?.commissionBalance.total_usdt_converted ?? 0) || 0);
  // Total withdrawal network fees actually charged: a flat network fee is deducted per
  // completed withdrawal but is NOT tracked inside total_withdrawn, so surface it here so
  // that Total Earned − Total Withdrawn − Fees − Converted reconciles to the balance.
  const completedWithdrawalCount = (affiliateData?.withdrawalHistory || []).filter(
    (w: any) => w.status === 'completed'
  ).length;
  const totalWithdrawalFees = completedWithdrawalCount * withdrawalNetworkFee;

  // Form states — BSC only (v4.0.53)
  const [withdrawalForm, setWithdrawalForm] = useState<WithdrawalFormData>({
    amount: '',
    walletAddress: '',
    network: 'USDT-BEP20'
  });

  // Locked wallet handlers
  const handleLockedWalletsLoaded = (wallets: LockedWallet[]) => {
    setLockedWallets(wallets);
    updateAffiliateWalletFromLocked(wallets, withdrawalForm.network);
  };

  // v4.7.18: Withdrawals accept BOTH centralized and decentralized wallets.
  // Prefer centralized when both exist, otherwise fall back to decentralized / legacy untyped.
  const updateAffiliateWalletFromLocked = (wallets: LockedWallet[], selectedNetwork: string) => {
    const lockedKey = NETWORK_TO_LOCKED_KEY[selectedNetwork];
    if (!lockedKey) { setWalletLocked(false); return; }
    const networkWallets = wallets.filter(w => normalizeWithdrawalNetwork(w.network) === normalizeWithdrawalNetwork(lockedKey) && w.is_active);
    const match =
      networkWallets.find(w => w.wallet_type === 'centralized') ||
      networkWallets.find(w => w.wallet_type === 'decentralized') ||
      networkWallets.find(w => !w.wallet_type);
    if (match) {
      setWithdrawalForm(prev => ({ ...prev, walletAddress: match.wallet_address }));
      setWalletLocked(true);
    } else {
      if (wallets.length > 0) {
        setWithdrawalForm(prev => ({ ...prev, walletAddress: '' }));
      }
      setWalletLocked(false);
    }
  };

  // Re-check locked wallet when network changes
  useEffect(() => {
    if (lockedWallets.length > 0) {
      updateAffiliateWalletFromLocked(lockedWallets, withdrawalForm.network);
    }
  }, [withdrawalForm.network]);
  const [conversionForm, setConversionForm] = useState<ConversionFormData>({
    usdtAmount: ''
  });
  const [transferForm, setTransferForm] = useState<TransferFormData>({
    recipientUsername: '',
    shareAmount: ''
  });

  // Loading states
  const [withdrawalLoading, setWithdrawalLoading] = useState(false);
  const [conversionLoading, setConversionLoading] = useState(false);
  const [_transferLoading, setTransferLoading] = useState(false);
  const [confirmingWithdrawal, setConfirmingWithdrawal] = useState<string | null>(null);

  // Pagination state - how many items to show for each transaction type
  const [visibleItems, setVisibleItems] = useState<PaginationState>({
    usdtTransactions: 10,
    shareTransactions: 10,
    conversions: 10,
    withdrawals: 10
  });

  // Validation states
  const [recipientValidation, setRecipientValidation] = useState<any>(null);

  // Success/error states
  const [operationSuccess, setOperationSuccess] = useState<string | null>(null);
  const [operationError, setOperationError] = useState<string | null>(null);
  const [isMobile, setIsMobile] = useState(false);

  const enteredWithdrawalAmount = parseFloat(withdrawalForm.amount);
  const validEnteredWithdrawalAmount = Number.isFinite(enteredWithdrawalAmount) ? enteredWithdrawalAmount : 0;
  const withdrawalAmountExceedsMax = validEnteredWithdrawalAmount > maxWithdrawalAmount;
  const setSafeWithdrawalAmount = (rawValue: string) => {
    if (rawValue === '') { setWithdrawalForm(prev => ({ ...prev, amount: '' })); return; }
    const nextAmount = parseFloat(rawValue);
    if (!Number.isFinite(nextAmount) || nextAmount < 0) { setWithdrawalForm(prev => ({ ...prev, amount: '' })); return; }
    if (nextAmount > maxWithdrawalAmount) {
      setWithdrawalForm(prev => ({ ...prev, amount: maxWithdrawalAmount > 0 ? formatFlooredUsd(maxWithdrawalAmount) : '' }));
      setOperationError(`Maximum available withdrawal amount is $${formatFlooredUsd(maxWithdrawalAmount)}.`);
      return;
    }
    setOperationError(null);
    setWithdrawalForm(prev => ({ ...prev, amount: rawValue }));
  };

  // System-wide withdrawal toggle state
  const [withdrawalsEnabled, setWithdrawalsEnabled] = useState(true);

  // Pending verification actions state (magic link only - no PIN)
  const [resendingVerification, setResendingVerification] = useState<string | null>(null);
  const [cancellingPendingId, setCancellingPendingId] = useState<string | null>(null);

  // Email verification modal state — shown immediately after successful withdrawal request
  const [withdrawalVerificationModal, setWithdrawalVerificationModal] = useState<WithdrawalConfirmationData | null>(null);

  // USDT commission top-up — generally available since v4.7.14.
  // The boolean is retained to keep the existing conditional render branches
  // structurally intact; the runtime value is always true post-v4.7.14.
  const isTopupAllowed = true;
  const [showTopupModal, setShowTopupModal] = useState(false);
  const [topupWallets, setTopupWallets] = useState<TopupCompanyWallet[]>([]);
  const [pendingTopups, setPendingTopups] = useState<PendingTopup[]>([]);
  const [topupNetwork, setTopupNetwork] = useState<string>('');
  const [_topupAmount, setTopupAmount] = useState('');
  const [topupTxHash, setTopupTxHash] = useState('');
  const [topupSenderWallet, setTopupSenderWallet] = useState('');
  const [isSubmittingTopup, setIsSubmittingTopup] = useState(false);
  const [topupError, setTopupError] = useState<string | null>(null);
  const [topupSuccess, setTopupSuccess] = useState<string | null>(null);
  const [topupLock, setTopupLock] = useState<{ amount: number; packages: { packageId: string; qty: number }[]; label: string } | null>(null);

  // 1-share top-up cap. The maximum a user may top up at any moment is the
  // delta between the current share price and (current balance + pending
  // top-ups already submitted). Mirrors computeTopupCap() in
  // api/user/commission-topup.js — the server is authoritative; this is UX.
  // Recomputed whenever affiliateData or pendingTopups change so the cap
  // reacts immediately when a previous top-up is approved or rejected.
  const topupCap = useMemo(() => {
    const sharePrice = parseFloat(affiliateData?.currentPhase?.price_per_share || '0') || 0;
    const currentBalance = Number(affiliateData?.commissionBalance?.usdt_balance || 0) || 0;
    const pendingTotal = (pendingTopups || [])
      .filter(t => t.status === 'pending')
      .reduce((sum, t) => sum + (parseFloat(String(t.topup_payment_amount || '0')) || 0), 0);
    const rawMax = sharePrice - currentBalance - pendingTotal;
    const maxAllowed = rawMax > 0 ? Math.round(rawMax * 100) / 100 : 0;
    return { sharePrice, currentBalance, pendingTotal, maxAllowed };
  }, [affiliateData, pendingTopups]);

  useEffect(() => {
    loadAffiliateData();
    loadKYCValidation();

    // Check if withdrawals are enabled system-wide
    const checkWithdrawalsEnabled = async () => {
      try {
        const headers = getSessionHeaders();
        const res = await fetch('/api/user/system-settings', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...headers },
          body: JSON.stringify({ operation: 'check_withdrawals_enabled' })
        });
        const result = await res.json();
        if (result.success) {
          setWithdrawalsEnabled(result.withdrawalsEnabled);
        }
      } catch (err) {
        console.warn('Could not check withdrawal status:', err);
      }
    };
    checkWithdrawalsEnabled();

    // Check for withdrawal confirmation parameter in URL
    const urlParams = new URLSearchParams(window.location.search);
    const confirmWithdrawalId = urlParams.get('confirm');
    if (confirmWithdrawalId) {
      // Switch to withdraw tab and show confirmation prompt
      setActiveTab('withdraw');
      // Remove the parameter from URL
      window.history.replaceState({}, '', window.location.pathname);
      // Show confirmation prompt after a brief delay
      setTimeout(() => {
        handleWithdrawalConfirmation(confirmWithdrawalId);
      }, 500);
    }
  }, [userId]);

  // Mobile detection
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);

    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const loadKYCValidation = async () => {
    try {
      const validation = await validateKYCForFinancialOperations(userId);
      setKycValidation(validation);
    } catch (error) {
      console.error('Error loading KYC validation:', error);
      setKycValidation({
        isValid: false,
        status: 'not_started',
        message: 'Error checking KYC status. Please try again.',
        canWithdraw: false,
        canReceiveDividends: false
      });
    }
  };

  const loadAffiliateData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Load all affiliate data from API
      const response = await fetch(`${API_BASE_URL}/api/user/affiliate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getSessionHeaders() },
        body: JSON.stringify({
          userId,
          operation: 'load_affiliate_manager_data'
        })
      });

      const apiResult = await response.json();
      if (!response.ok) throw new Error(apiResult.error);

      const {
        userData,
        commissionBalance,
        referrals,
        transactions,
        referredUsers,
        withdrawalHistory,
        conversionHistory,
        sharePurchases,
        sentTransfers,
        receivedTransfers,
        currentPhase
      } = apiResult.data;

      // Map referred users to transactions
      const transactionsWithUsers = transactions.map((transaction: any) => ({
        ...transaction,
        referred_user: referredUsers?.find((user: any) => user.id === transaction.referred_id)
      }));

      // Calculate purchased shares (excluding commission conversions)
      const __purchasedShares = sharePurchases?.reduce((sum: number, purchase: any) => {
        if (purchase.status === 'active' && purchase.payment_method !== 'Commission Conversion') {
          return sum + (purchase.shares_purchased || 0);
        }
        return sum;
      }, 0) || 0;

      const purchasedAmount = sharePurchases?.reduce((sum: number, purchase: any) => {
        if (purchase.status === 'active' && purchase.payment_method !== 'Commission Conversion') {
          return sum + (purchase.total_amount || 0);
        }
        return sum;
      }, 0) || 0;

      // Calculate converted shares from approved conversions
      const approvedConversions = conversionHistory?.filter((c: any) => c.status === 'approved') || [];
      const totalUsdtConverted = approvedConversions.reduce((sum: number, conversion: any) => sum + parseFloat(conversion.usdt_amount), 0);

      // Combine transfer history
      const transferHistory = [...(sentTransfers || []), ...(receivedTransfers || [])];

      // Calculate net transferred shares
      const __totalSharesSent = sentTransfers?.reduce((sum: number, transfer: any) => sum + parseFloat(transfer.shares_transferred), 0) || 0;
      const __totalSharesReceived = receivedTransfers?.reduce((sum: number, transfer: any) => sum + parseFloat(transfer.shares_transferred), 0) || 0;

      // Calculate referral stats
      const totalReferrals = referrals?.length || 0;
      const activeReferrals = referrals?.filter((r: any) => r.status === 'active').length || 0;
      const totalCommissionEarned = (commissionBalance?.total_earned_usdt || 0) +
                                   ((commissionBalance?.total_earned_shares || 0) * parseFloat(currentPhase?.price_per_share || 0));

      // Use UNIFIED SHARE CALCULATION SERVICE
      const shareBreakdown: ShareBreakdown = await calculateUserShares(userId);
      const totalSharesOwned = shareBreakdown.totalShares;

      console.log('📊 COMPREHENSIVE AFFILIATE MANAGER - UNIFIED SHARE CALCULATION:', {
        userId: userId,
        username: shareBreakdown.username,
        directPurchases: shareBreakdown.directPurchases,
        bonusShares: shareBreakdown.bonusShares,
        commissionLevelShares: shareBreakdown.commissionLevelShares,
        presaleCommissionShares: shareBreakdown.presaleCommissionShares,
        convertedShares: shareBreakdown.convertedShares,
        transfersReceived: shareBreakdown.transfersReceived,
        transfersSent: shareBreakdown.transfersSent,
        netTransfers: shareBreakdown.netTransfers,
        legacyShares: shareBreakdown.legacyShares,
        TOTAL: shareBreakdown.totalShares,
        formula: 'purchases + bonus + commLevel + presale + converted + received - sent + legacy'
      });

      setAffiliateData({
        user: userData,
        referralCount: totalReferrals,
        commissionBalance: commissionBalance ? {
          ...commissionBalance,
          total_converted_shares: shareBreakdown.convertedShares,
          total_usdt_converted: totalUsdtConverted,
          total_shares_owned: totalSharesOwned,
          purchased_shares: shareBreakdown.directPurchases + shareBreakdown.bonusShares,
          purchased_amount: purchasedAmount,
          shares_sent: shareBreakdown.transfersSent,
          shares_received: shareBreakdown.transfersReceived,
          net_transferred_shares: shareBreakdown.netTransfers
        } : {
          usdt_balance: 0,
          share_balance: 0,
          total_earned_usdt: 0,
          total_earned_shares: 0,
          total_withdrawn: 0,
          displayed_balance: 0,
          computed_withdrawable_balance: 0,
          withdrawal_request_max_usdt: 0,
          source_of_funds_ceiling: 0,
          network_fee: 2,
          pending_withdrawal_count: 0,
          withdrawal_blocked: false,
          withdrawal_block_reason_code: null,
          withdrawal_block_reason_message: null,
          total_converted_shares: 0,
          total_usdt_converted: 0,
          total_shares_owned: 0,
          purchased_shares: 0,
          purchased_amount: 0,
          shares_sent: 0,
          shares_received: 0,
          net_transferred_shares: 0
        },
        referralStats: {
          total_referrals: totalReferrals,
          active_referrals: activeReferrals,
          total_commission_earned: totalCommissionEarned
        },
        recentTransactions: transactionsWithUsers || [],
        withdrawalHistory: withdrawalHistory || [],
        conversionHistory: conversionHistory || [],
        transferHistory: transferHistory || [],
        purchaseHistory: sharePurchases || [],
        currentPhase
      });

    } catch (error) {
      console.error('Error loading affiliate data:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to load affiliate data';
      console.error('Detailed error:', errorMessage);
      setError(`Failed to load affiliate data: ${errorMessage}`);
    } finally {
      setLoading(false);
    }
  };

  const handleDataRefresh = async () => {
    await loadAffiliateData();
    await loadKYCValidation();
    if (onDataRefresh) {
      onDataRefresh();
    }
  };

  // Clear operation messages after 5 seconds
  useEffect(() => {
    if (operationSuccess || operationError) {
      const timer = setTimeout(() => {
        setOperationSuccess(null);
        setOperationError(null);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [operationSuccess, operationError]);

  // Handle USDT withdrawal request
  const handleWithdrawalSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setOperationError(null);
    setOperationSuccess(null);

    // Check for approved withdrawals pending confirmation
    const pendingConfirmation = affiliateData?.withdrawalHistory.filter(
      w => w.status === 'approved' && !w.user_confirmed_receipt
    );

    if (pendingConfirmation && pendingConfirmation.length > 0) {
      const pendingAmount = pendingConfirmation.reduce((sum: number, w: any) => sum + parseFloat(w.withdrawal_amount), 0);
      alert(
        `⚠️ You have ${pendingConfirmation.length} approved withdrawal(s) totaling $${pendingAmount.toFixed(2)} pending your confirmation.\n\n` +
        `Please scroll down to the "Withdrawal Approved" section and click "Confirm Receipt" after you have received the funds in your wallet.\n\n` +
        `You cannot request a new withdrawal until you confirm receipt of your previous withdrawal(s).`
      );
      return;
    }

    // Validate userId first
    if (!userId || userId <= 0) {
      console.error('❌ Invalid userId for withdrawal:', userId);
      setOperationError('Session error. Please log out and log back in to continue.');
      return;
    }

    // SECURITY: Block withdrawal for protected admin accounts
    const PROTECTED_ADMIN_IDS = [1,4,80,89,102,103,104,105,107,111,113,115,123];
    if (PROTECTED_ADMIN_IDS.includes(userId)) {
      setOperationError('Withdrawal operations are not available for admin/system accounts.');
      return;
    }

    if (!kycValidation?.canWithdraw) {
      setOperationError('KYC verification required for withdrawals.');
      return;
    }

    const amount = parseFloat(withdrawalForm.amount);
    // Network fee: $10 for ERC20 (Ethereum), $2 for others
    const networkFee = withdrawalForm.network === 'USDT-ERC20' ? 10.00 : 2.00;

    if (!amount || amount <= 0) {
      setOperationError('Please enter a valid withdrawal amount.');
      return;
    }

    if (amount > maxWithdrawalAmount) {
      setOperationError(`Maximum available withdrawal amount is $${formatFlooredUsd(maxWithdrawalAmount)}.`);
      return;
    }

    if (amount < 5) {
      setOperationError(`Minimum withdrawal amount is $5.00 (plus $${formatFlooredUsd(networkFee)} network fee)`);
      return;
    }

    if (!withdrawalForm.walletAddress.trim()) {
      setOperationError('Please enter your wallet address.');
      return;
    }

    setWithdrawalLoading(true);

    try {
      // Use the existing withdrawal escrow service
      const { withdrawalEscrowService } = await import('../../lib/services/withdrawalEscrowService');

      // Map network format to database expected format
      const networkMapping: { [key: string]: string } = {
        'USDT-TRC20': 'TRON',
        'USDT-ERC20': 'ETH',
        'USDT-BEP20': 'BSC',
        'USDT-POL': 'POL'
      };

      const dbNetwork = networkMapping[withdrawalForm.network] || withdrawalForm.network;

      const result = await withdrawalEscrowService.submitWithdrawalRequest({
        user_id: userId,
        withdrawal_amount: amount,
        wallet_address: withdrawalForm.walletAddress,
        network: dbNetwork,
        currency: 'USDT'
      });

      if (!result.success) {
        throw new Error(result.error || 'Failed to submit withdrawal request');
      }

      console.log('✅ Withdrawal request submitted via escrow service:', result.withdrawalRequestId);

      // Magic link verification flow — show non-dismissible verification modal
      if (result.requiresVerification && result.withdrawalRequestId) {
        const submittedAmount = amount;
        const submittedWallet = withdrawalForm.walletAddress;
        const submittedNetwork = dbNetwork;
        setWithdrawalForm({ amount: '', walletAddress: '', network: 'USDT-BEP20' });
        // Refresh data in background (will not unmount the modal since modal is rendered at component root level)
        handleDataRefresh();
        // Show the non-dismissible email verification modal immediately
        setWithdrawalVerificationModal({
          withdrawalId: result.withdrawalRequestId,
          amount: submittedAmount,
          walletAddress: submittedWallet,
          network: submittedNetwork,
          linkSent: result.linkSent !== false, // default to true unless explicitly false
          expiresAt: result.expiresAt || null,
        });
        return;
      }

      setOperationSuccess(`Withdrawal request submitted successfully! You will receive an email confirmation shortly.`);
      setWithdrawalForm({ amount: '', walletAddress: '', network: 'USDT-BEP20' });
      await handleDataRefresh();

    } catch (error: any) {
      console.error('❌ Error submitting withdrawal request:', error);

      // Provide more specific error messages
      let errorMessage = 'Failed to submit withdrawal request. Please try again.';

      if (error.code === '42501') {
        errorMessage = 'Authentication error. Please log out and log back in, then try again.';
      } else if (error.message?.includes('auth')) {
        errorMessage = 'Authentication required. Please log out and log back in.';
      } else if (error.message?.includes('User balance not found')) {
        errorMessage = 'Your commission balance record was not found. Please contact support.';
      } else if (error.message?.includes('user_id') || error.message?.includes('user not found')) {
        errorMessage = 'Session expired. Please log out and log back in to continue.';
      } else if (error.message?.includes('pending withdrawal')) {
        errorMessage = error.message; // Already user-friendly
      } else if (error.message?.includes('Insufficient')) {
        errorMessage = error.message; // Already user-friendly
      } else if (error.message) {
        errorMessage = error.message;
      }

      setOperationError(errorMessage);
    } finally {
      setWithdrawalLoading(false);
    }
  };

  // Handle withdrawal confirmation
  const handleWithdrawalConfirmation = async (withdrawalId: string) => {
    // Check if this withdrawal is in the approved state and belongs to the user
    const withdrawal = affiliateData?.withdrawalHistory.find(
      w => w.id === withdrawalId && w.status === 'approved'
    );

    if (!withdrawal) {
      setOperationError('Withdrawal not found or already confirmed.');
      return;
    }

    const confirmed = window.confirm(
      `Have you received $${parseFloat(withdrawal.withdrawal_amount).toFixed(2)} USDT in your wallet?\n\n` +
      `Wallet: ${withdrawal.wallet_address}\n` +
      `Network: ${withdrawal.network}\n\n` +
      `Click OK to confirm receipt, or Cancel if you haven't received the funds yet.`
    );

    if (!confirmed) {
      return;
    }

    setConfirmingWithdrawal(withdrawalId);
    setOperationError(null);
    setOperationSuccess(null);

    try {
      const { withdrawalEscrowService } = await import('../../lib/services/withdrawalEscrowService');

      const result = await withdrawalEscrowService.confirmReceipt(withdrawalId, userId);

      if (!result.success) {
        throw new Error(result.error || 'Failed to confirm withdrawal receipt');
      }

      setOperationSuccess('✅ Thank you for confirming! Your withdrawal is now complete.');
      await handleDataRefresh();

    } catch (error: any) {
      console.error('❌ Error confirming withdrawal:', error);
      setOperationError(error.message || 'Failed to confirm withdrawal receipt. Please try again.');
    } finally {
      setConfirmingWithdrawal(null);
    }
  };

  // Handle resending verification link for a pending_verification withdrawal
  const handleResendVerification = async (withdrawalId: string) => {
    setResendingVerification(withdrawalId);
    try {
      // SECURITY (v2.9.12): Use session headers — userId is derived server-side from session
      const response = await fetch('/api/resend-withdrawal-verification', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getSessionHeaders() },
        body: JSON.stringify({ withdrawalId })
      });

      const result = await response.json();

      if (result.success) {
        setOperationSuccess('✅ A new verification code has been sent to your email. Please check your inbox.');
      } else {
        setOperationError(`Failed to resend verification: ${result.error}`);
      }
    } catch (error: any) {
      console.error('Error resending verification:', error);
      setOperationError('Failed to resend verification code. Please try again.');
    } finally {
      setResendingVerification(null);
    }
  };

  // Handle cancelling a pending_verification withdrawal (from the list, not from PIN UI)
  const handleCancelPendingWithdrawal = async (withdrawalId: string) => {
    if (!confirm('Are you sure you want to cancel this withdrawal? Your funds will be refunded to your balance.')) {
      return;
    }

    setCancellingPendingId(withdrawalId);
    try {
      // Use userId prop for validation (magic link users don't have Supabase Auth sessions)
      const response = await fetch('/api/cancel-withdrawal', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getSessionHeaders() },
        body: JSON.stringify({ withdrawalId, userId })
      });

      const result = await response.json();

      if (result.success) {
        setOperationSuccess('✅ Withdrawal cancelled. Your funds have been refunded to your balance.');
        await handleDataRefresh();
      } else {
        setOperationError(`Failed to cancel: ${result.error}`);
      }
    } catch (error: any) {
      console.error('Error cancelling withdrawal:', error);
      setOperationError('Failed to cancel withdrawal. Please try again.');
    } finally {
      setCancellingPendingId(null);
    }
  };

  const describePackageConversion = (resultData: any, paidShares: number, usdtUsed: number, leftover: number) => {
    if (resultData?.pending) {
      return `Commission of $${usdtUsed.toFixed(2)} is reserved for ${paidShares} shares. An admin must verify your commission before the shares are added.`;
    }
    let message = `Converted $${usdtUsed.toFixed(2)} into ${paidShares} paid shares.`;
    if (resultData?.giftsIssued) {
      message += ` Bonus Shares +${resultData.buyerBonus} are in your account.`;
      message += resultData.sponsorGiftIssued
        ? ` Sponsor bonus +${resultData.sponsorBonus} is in your sponsor's account.`
        : ' Sponsor bonus was not credited.';
    } else if (resultData?.giftAlert) {
      message += ' Package bonus was not credited. Support has been alerted. Your paid shares are in your account.';
    }
    if (leftover > 0.009) message += ` $${leftover.toFixed(2)} remains in your commission balance.`;
    return message;
  };

  const handlePackageConversion = async (cart: CommissionPackageCart) => {
    setOperationError(null);
    setOperationSuccess(null);
    if (!affiliateData?.currentPhase) {
      setOperationError('Unable to get current share price. Please try again.');
      return;
    }
    if (cart.shortfall > 0.01) {
      setOperationError(`This order still needs $${cart.shortfall.toFixed(2)}. Top up that amount, then convert after it is approved.`);
      return;
    }
    if (cart.costUsd > rawUsdtBalance + cart.shortfall + 0.000001) {
      setOperationError(`Insufficient available balance. Available: $${rawUsdtBalance.toFixed(2)}`);
      return;
    }
    setConversionLoading(true);
    try {
      const result = await affiliateTransactionService.processCommissionToSharesConversion({
        userId,
        usdtAmount: cart.costUsd,
        sharesReceived: cart.paidShares,
        sharePrice: cart.phasePrice,
        phaseId: affiliateData.currentPhase.id,
        packages: cart.lines.map((line) => ({ packageId: line.packageId as 'P1' | 'P2' | 'P3' | 'P4' | 'P5', qty: line.qty })),
      });
      if (!result.success) throw new Error(result.error || 'Conversion failed');
      const leftover = rawUsdtBalance - cart.costUsd;
      setOperationSuccess(describePackageConversion(result.data, cart.paidShares, cart.costUsd, leftover));
      await handleDataRefresh();
    } catch (error: any) {
      console.error('Error processing package conversion:', error);
      setOperationError(error.message || 'Failed to process conversion. Please try again.');
    } finally {
      setConversionLoading(false);
    }
  };

  // Handle commission to shares conversion
  const handleConversionSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setOperationError(null);
    setOperationSuccess(null);

    const usdtAmount = parseFloat(conversionForm.usdtAmount);
    if (!usdtAmount || usdtAmount <= 0) {
      setOperationError('Please enter a valid USDT amount.');
      return;
    }

    const conversionAvailableUsdt = rawUsdtBalance;

    if (usdtAmount > conversionAvailableUsdt) {
      setOperationError(`Insufficient available balance. Available: $${conversionAvailableUsdt.toFixed(2)}`);
      return;
    }

    if (!affiliateData?.currentPhase) {
      setOperationError('Unable to get current share price. Please try again.');
      return;
    }

    const sharePrice = parseFloat(affiliateData.currentPhase.price_per_share);
    if (usdtAmount < sharePrice) {
      setOperationError(`Minimum conversion amount is $${sharePrice.toFixed(2)} (1 share at current phase price)`);
      return;
    }

    const sharesReceived = Math.floor(usdtAmount / sharePrice); // Only whole shares
    const usdtUsed = sharesReceived * sharePrice;
    const remainingUSDT = usdtAmount - usdtUsed;

    // Prevent conversion if no whole shares can be purchased
    if (sharesReceived === 0) {
      setOperationError(`Insufficient USDT for 1 whole share. You need at least $${sharePrice.toFixed(2)} USDT.`);
      setConversionLoading(false);
      return;
    }

    setConversionLoading(true);

    try {
      const conversionTransaction = {
        userId,
        usdtAmount: usdtUsed, // Only use USDT needed for whole shares
        sharesReceived,
        sharePrice,
        phaseId: affiliateData.currentPhase.id
      };

      const result = await affiliateTransactionService.processCommissionToSharesConversion(conversionTransaction);

      if (!result.success) {
        throw new Error(result.error || 'Conversion failed');
      }

      setOperationSuccess(`Your conversion is pending admin review. $${usdtUsed.toFixed(2)} of commission is reserved for ${sharesReceived} shares. The shares are not in your account until an admin verifies the commission.${remainingUSDT > 0 ? ` $${remainingUSDT.toFixed(2)} USDT remains available.` : ''}`);
      setConversionForm({ usdtAmount: '' });
      await handleDataRefresh();

    } catch (error: any) {
      console.error('Error processing conversion:', error);
      setOperationError(error.message || 'Failed to process conversion. Please try again.');
    } finally {
      setConversionLoading(false);
    }
  };

  // Handle share transfer
  const __handleTransferSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    setOperationError(null);
    setOperationSuccess(null);

    // SECURITY FIX: Use strict Number validation instead of parseFloat
    const shareAmount = Number(transferForm.shareAmount);
    if (!Number.isFinite(shareAmount) || shareAmount <= 0 || isNaN(shareAmount)) {
      setOperationError('Please enter a valid share amount (positive number only).');
      return;
    }

    // FIXED: Use total_shares_owned instead of just share_balance to include purchased shares
    const totalAvailableShares = affiliateData?.commissionBalance.total_shares_owned || 0;

    // Check if user has enough shares (no transfer fee)
    if (shareAmount > totalAvailableShares) {
      setOperationError(`Insufficient share balance. Need ${shareAmount.toFixed(2)} shares, but only have ${totalAvailableShares.toFixed(2)} available.`);
      return;
    }

    // Minimum transfer amount is 1 whole share (transfers use whole shares only)
    if (shareAmount < 1) {
      setOperationError('Minimum transfer amount is 1 whole share');
      return;
    }

    if (!transferForm.recipientUsername.trim()) {
      setOperationError('Please enter recipient username.');
      return;
    }

    if (!recipientValidation?.isValid) {
      setOperationError('Please enter a valid recipient username.');
      return;
    }

    // 🔒 SECURITY: Prevent self-transfer (except for userID 4)
    if (recipientValidation.userId === userId && userId !== 4) {
      setOperationError('You cannot transfer shares to yourself');
      return;
    }

    setTransferLoading(true);

    try {
      // SECURITY FIX: Use API endpoint instead of client-side service role
      const lookupResponse = await fetch('/api/lookup-transfer-recipient', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getSessionHeaders() },
        body: JSON.stringify({
          username: transferForm.recipientUsername.trim(),
          senderId: userId
        })
      });

      const lookupResult = await lookupResponse.json();
      console.log('🔍 TRANSFER DEBUG: lookupResult =', JSON.stringify(lookupResult));

      if (!lookupResult.success || !lookupResult.recipient) {
        throw new Error(lookupResult.error || 'Recipient user not found');
      }

      console.log('✅ TRANSFER DEBUG: recipient found, hasKYC =', lookupResult.recipient.hasKYC, '(NOT blocking transfer)');
      // KYC check removed - users can transfer shares without KYC verification

      // No transfer fee - shares transferred = shares received
      const transferTransaction = {
        senderId: userId,
        recipientId: lookupResult.recipient.id,
        sharesTransferred: shareAmount
      };

      const result = await affiliateTransactionService.processShareTransfer(transferTransaction);

      if (!result.success) {
        throw new Error(result.error || 'Transfer failed');
      }

      setOperationSuccess(`Successfully transferred ${shareAmount.toFixed(2)} shares to ${transferForm.recipientUsername}!`);
      setTransferForm({ recipientUsername: '', shareAmount: '' });
      setRecipientValidation(null);
      await handleDataRefresh();

    } catch (error: any) {
      console.error('Error processing transfer:', error);
      setOperationError(error.message || 'Failed to process transfer. Please try again.');
    } finally {
      setTransferLoading(false);
    }
  };

  // Handle recipient username validation
  const __handleRecipientUsernameChange = (value: string) => {
    setTransferForm(prev => ({ ...prev, recipientUsername: value }));

    if (value.trim().length > 0) {
      // Use validateUserExists to check if username EXISTS (for transfers)
      // validateUsername checks if username is AVAILABLE (for registration)
      // validateUserExists checks if username EXISTS (for transfers to any user)
      realtimeValidation.validateUserExists(value.trim(), setRecipientValidation);
    } else {
      setRecipientValidation(null);
    }
  };

  // ---------------------------------------------------------------------------
  // USDT commission top-up helpers (allow-listed users only)
  // ---------------------------------------------------------------------------
  const callTopupApi = useCallback(async (operation: string, data: Record<string, any> = {}) => {
    const r = await fetch(`${API_BASE_URL}/api/user/commission-topup`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getSessionHeaders() },
      body: JSON.stringify({ operation, data })
    });
    const j = await r.json();
    if (!r.ok || j?.success === false) throw new Error(j?.error || `Top-up API error (${operation})`);
    return j?.data;
  }, []);

  const loadPendingTopups = useCallback(async () => {
    try {
      const d = await callTopupApi('load_pending_topups');
      setPendingTopups(d?.topups || []);
    } catch (e) { console.warn('Failed to load pending top-ups:', e); }
  }, [callTopupApi]);

  useEffect(() => {
    if (!isTopupAllowed) return;
    (async () => {
      try {
        const d = await callTopupApi('load_crypto_wallets');
        const wallets: TopupCompanyWallet[] = d?.wallets || [];
        setTopupWallets(wallets);
        if (wallets.length > 0 && !topupNetwork) setTopupNetwork(wallets[0].network);
      } catch (e) { console.warn('Failed to load crypto wallets:', e); }
      await loadPendingTopups();
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, isTopupAllowed]);

  const openTopupModal = () => {
    setTopupError(null);
    setTopupSuccess(null);
    setTopupLock(null);
    setTopupAmount(topupCap.maxAllowed > 0 ? topupCap.maxAllowed.toFixed(2) : '');
    setTopupTxHash('');
    setTopupSenderWallet('');
    setShowTopupModal(true);
  };

  const openPackageTopup = (cart: CommissionPackageCart) => {
    setTopupError(null);
    setTopupSuccess(null);
    setTopupLock({
      amount: cart.shortfall,
      packages: cart.lines.map((line) => ({ packageId: line.packageId, qty: line.qty })),
      label: cart.lines.map((line) => `${line.name} × ${line.qty}`).join(', '),
    });
    setTopupAmount(cart.shortfall.toFixed(2));
    setTopupTxHash('');
    setTopupSenderWallet('');
    setShowTopupModal(true);
  };

  const submitTopup = async () => {
    // KYC intentionally NOT required for top-up: it only credits the user's
    // commission balance from USDT they have already sent externally. The
    // downstream withdrawal flow keeps its own KYC gate.
    // Amount is locked to the server-computed 1-share cap; we send it directly
    // (not parseFloat of a user input) so the UI cannot be coerced into
    // submitting a different value via devtools. The server re-validates.
    const lockedAmount = topupLock?.amount ?? topupCap.maxAllowed;
    if (topupCap.sharePrice <= 0) { setTopupError('Unable to determine current share price. Please try again.'); return; }
    if (lockedAmount <= 0) {
      setTopupError('Your commission balance already covers this order. Convert to shares before submitting another top-up.');
      return;
    }
    const amount = lockedAmount;
    if (!topupTxHash || topupTxHash.trim().length < 10) { setTopupError('Transaction hash (TXID) is required.'); return; }
    if (!topupSenderWallet || topupSenderWallet.trim().length < 10) { setTopupError('Sender wallet address is required.'); return; }
    if (!topupNetwork) { setTopupError('Select a network.'); return; }
    setIsSubmittingTopup(true);
    setTopupError(null);
    try {
      await callTopupApi('submit_crypto_payment', {
        phaseId: affiliateData?.currentPhase?.id,
        topupAmount: amount,
        transactionHash: topupTxHash.trim(),
        senderWallet: topupSenderWallet.trim(),
        networkName: topupNetwork,
        packages: topupLock?.packages
      });
      setTopupSuccess('Top-up submitted. An admin will confirm the payment. If your balance can buy the packages you selected, those packages are purchased when the top-up is approved.');
      setTopupAmount(''); setTopupTxHash(''); setTopupSenderWallet('');
      await loadPendingTopups();
      setTimeout(() => { setShowTopupModal(false); setTopupSuccess(null); }, 2500);
    } catch (e: any) {
      setTopupError(e.message || 'Failed to submit top-up.');
    } finally {
      setIsSubmittingTopup(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-gray-800 rounded-lg border border-gray-700 p-8">
        <div className="flex items-center justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-yellow-500"></div>
          <span className="ml-3 text-gray-300">Loading affiliate data...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-900/20 border border-red-500/30 rounded-lg p-6">
        <div className="flex items-center space-x-3">
          <div className="text-2xl">❌</div>
          <div>
            <h3 className="font-semibold text-white mb-1">Error Loading Data</h3>
            <p className="text-red-300">{error}</p>
            <button
              onClick={loadAffiliateData}
              className="mt-3 bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg text-sm transition-colors"
            >
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!affiliateData) {
    return null;
  }

  const tabs = [
    { id: 'overview', label: '📊 Overview', icon: '📊' },
    { id: 'transactions', label: '💰 Transactions', icon: '💰' },
    { id: 'convert', label: '🔄 Convert USDT', icon: '🔄' },
    { id: 'transfer', label: '📤 Transfer Shares', icon: '📤' },
    { id: 'withdraw', label: '💸 Withdraw', icon: '💸' },
    { id: 'history', label: '📋 History', icon: '📋' },
    { id: 'notifications', label: '🔔 Notifications', icon: '🔔' }
  ];

  return (
    <div className="commission-mobile-container space-y-4 sm:space-y-6 p-2 sm:p-0" translate="no">
      {/* Tab Navigation */}
      <div className="bg-gray-800 rounded-lg border border-gray-700 p-1">
        <div className="commission-tab-nav flex gap-1 overflow-x-auto scrollbar-hide" style={{
          WebkitOverflowScrolling: 'touch',
          scrollbarWidth: 'none',
          msOverflowStyle: 'none'
        }}>
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id as any)}
              className={`commission-tab-btn flex-shrink-0 ${isMobile ? 'px-2 py-1 text-sm' : 'px-4 py-3 text-sm'} rounded-lg font-medium transition-colors whitespace-nowrap ${
                activeTab === tab.id
                  ? 'bg-yellow-600 text-black'
                  : 'text-gray-300 hover:text-white hover:bg-gray-700'
              }`}
              style={{
                minWidth: 'auto',
                fontSize: isMobile ? '10px' : '14px'
              }}
            >
              <span className="mr-1 text-sm sm:text-base">{tab.icon}</span>
              <span className="hidden sm:inline text-sm sm:text-sm">{tab.label}</span>
              <span className="sm:hidden text-sm">{tab.label.split(' ')[1] || tab.label.split(' ')[0]}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Tab Content */}
      <div className="min-h-[400px]">
        {activeTab === 'overview' && (
          <div className="space-y-4 sm:space-y-6">
            {/* Commission Summary Cards */}
            <div className={`commission-cards-grid grid gap-3 sm:gap-4 ${isMobile ? 'grid-cols-1' : 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5'}`}>
              {/* USDT Commissions */}
              <div className={`bg-gray-800 rounded-lg border border-gray-700 ${isMobile ? 'p-3' : 'p-4 sm:p-6'}`}>
                <div className="flex items-center gap-2 sm:gap-3 mb-3 sm:mb-4">
                  <span className={isMobile ? 'text-lg' : 'text-xl sm:text-2xl'}>💰</span>
                  <div className="min-w-0 flex-1">
                    <h3 className={`font-bold text-white truncate ${isMobile ? 'text-sm' : 'text-sm sm:text-lg'}`}>USDT Commissions</h3>
                    <p className={`text-white ${isMobile ? 'text-sm' : 'text-sm sm:text-sm'}`}>Cash earnings available</p>
                  </div>
                </div>
                <div className={`font-bold text-green-400 mb-2 ${isMobile ? 'text-lg' : 'text-xl sm:text-2xl'}`}>
                  ${rawUsdtBalance.toFixed(2)}
                </div>
                <div className="text-gray-300 text-sm sm:text-sm mb-2 sm:mb-3">Available Balance</div>
                <div className="text-sm text-white">
                  Total Earned: ${affiliateData.commissionBalance.total_earned_usdt.toFixed(2)}
                </div>
              </div>

              {/* Share Commissions - Presale Bonus (Ended 6 Nov 2025) */}
              <div className="bg-gray-800 rounded-lg border border-gray-700 p-4 sm:p-6">
                <div className="flex items-center gap-2 sm:gap-3 mb-3 sm:mb-4">
                  <span className="text-xl sm:text-2xl">🎁</span>
                  <div className="min-w-0 flex-1">
                    <h3 className="text-sm sm:text-lg font-bold text-white truncate">Presale Bonus Shares</h3>
                    <p className="text-white text-sm sm:text-sm">Presale bonus ended 6 Nov 2025</p>
                  </div>
                </div>
                <div className="text-xl sm:text-2xl font-bold text-yellow-400 mb-2">
                  {(affiliateData.commissionBalance.total_earned_shares || 0).toFixed(2)}
                </div>
                <div className="text-gray-300 text-sm sm:text-sm mb-2 sm:mb-3">Shares Received</div>
                <div className="text-sm text-white">
                  Value: ${((affiliateData.commissionBalance.total_earned_shares || 0) * parseFloat(affiliateData.currentPhase?.price_per_share || '10')).toFixed(2)}
                </div>
              </div>

              {/* Purchased Shares */}
              <div className="bg-gray-800 rounded-lg border border-gray-700 p-4 sm:p-6">
                <div className="flex items-center gap-2 sm:gap-3 mb-3 sm:mb-4">
                  <span className="text-xl sm:text-2xl">🛒</span>
                  <div className="min-w-0 flex-1">
                    <h3 className="text-sm sm:text-lg font-bold text-white truncate">Purchased Shares</h3>
                    <p className="text-white text-sm sm:text-sm">Direct share purchases</p>
                  </div>
                </div>
                <div className="text-xl sm:text-2xl font-bold text-blue-400 mb-2">
                  {(affiliateData.commissionBalance.purchased_shares || 0).toFixed(2)}
                </div>
                <div className="text-gray-300 text-sm sm:text-sm mb-2 sm:mb-3">Shares Purchased</div>
                <div className="text-sm text-white">
                  Amount paid: ${(affiliateData.commissionBalance.purchased_amount || 0).toFixed(2)}
                </div>
              </div>

              {/* Converted USDT to Shares */}
              <div className="bg-gray-800 rounded-lg border border-gray-700 p-4 sm:p-6">
                <div className="flex items-center gap-2 sm:gap-3 mb-3 sm:mb-4">
                  <span className="text-xl sm:text-2xl">🔄</span>
                  <div className="min-w-0 flex-1">
                    <h3 className="text-sm sm:text-lg font-bold text-white truncate">Converted USDT</h3>
                    <p className="text-white text-sm sm:text-sm">USDT converted to shares</p>
                  </div>
                </div>
                <div className="text-xl sm:text-2xl font-bold text-blue-400 mb-2">
                  {(affiliateData.commissionBalance.total_converted_shares || 0).toFixed(2)}
                </div>
                <div className="text-gray-300 text-sm sm:text-sm mb-2 sm:mb-3">Shares Purchased</div>
                <div className="text-sm text-white">
                  USDT Used: ${(affiliateData.commissionBalance.total_usdt_converted || 0).toFixed(2)}
                </div>
              </div>

              {/* Total Shares */}
              <div className="bg-gray-800 rounded-lg border border-gray-700 p-4 sm:p-6">
                <div className="flex items-center gap-2 sm:gap-3 mb-3 sm:mb-4">
                  <span className="text-xl sm:text-2xl">📊</span>
                  <div className="min-w-0 flex-1">
                    <h3 className="text-sm sm:text-lg font-bold text-white truncate">Total Shares</h3>
                    <p className="text-white text-sm sm:text-sm">All shares owned</p>
                  </div>
                </div>
                <div className="text-xl sm:text-2xl font-bold text-blue-400 mb-2">
                  {(affiliateData.commissionBalance.total_shares_owned || 0).toFixed(2)}
                </div>
                <div className="text-gray-300 text-sm sm:text-sm mb-2 sm:mb-3">Total Shares Owned</div>
                <div className="text-sm text-white space-y-1">
                  <div className="hidden sm:block">Purchased: {(affiliateData.commissionBalance.purchased_shares || 0).toFixed(2)} | Bonus: {affiliateData.commissionBalance.share_balance.toFixed(2)} | Converted: {(affiliateData.commissionBalance.total_converted_shares || 0).toFixed(2)}</div>
                  <div className="sm:hidden space-y-1">
                    <div>Purchased: {(affiliateData.commissionBalance.purchased_shares || 0).toFixed(2)}</div>
                    <div>Bonus: {affiliateData.commissionBalance.share_balance.toFixed(2)}</div>
                    <div>Converted: {(affiliateData.commissionBalance.total_converted_shares || 0).toFixed(2)}</div>
                  </div>
                  {((affiliateData.commissionBalance.shares_received || 0) > 0 || (affiliateData.commissionBalance.shares_sent || 0) > 0) && (
                    <div className={`${(affiliateData.commissionBalance.net_transferred_shares || 0) >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                      <div className="hidden sm:block">
                        Net Transfers: {(affiliateData.commissionBalance.net_transferred_shares || 0) >= 0 ? '+' : ''}{(affiliateData.commissionBalance.net_transferred_shares || 0).toFixed(2)}
                        (Received: {(affiliateData.commissionBalance.shares_received || 0).toFixed(2)} | Sent: {(affiliateData.commissionBalance.shares_sent || 0).toFixed(2)})
                      </div>
                      <div className="sm:hidden space-y-1">
                        <div>Net: {(affiliateData.commissionBalance.net_transferred_shares || 0) >= 0 ? '+' : ''}{(affiliateData.commissionBalance.net_transferred_shares || 0).toFixed(2)}</div>
                        <div>Received: {(affiliateData.commissionBalance.shares_received || 0).toFixed(2)}</div>
                        <div>Sent: {(affiliateData.commissionBalance.shares_sent || 0).toFixed(2)}</div>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Total Withdrawals */}
              <div className="bg-gray-800 rounded-lg border border-gray-700 p-4 sm:p-6">
                <div className="flex items-center gap-2 sm:gap-3 mb-3 sm:mb-4">
                  <span className="text-xl sm:text-2xl">💸</span>
                  <div className="min-w-0 flex-1">
                    <h3 className="text-sm sm:text-lg font-bold text-white truncate">Total Withdrawals</h3>
                    <p className="text-white text-sm sm:text-sm">USDT successfully withdrawn</p>
                  </div>
                </div>
                <div className="text-xl sm:text-2xl font-bold text-red-400 mb-2">
                  ${affiliateData.commissionBalance.total_withdrawn.toFixed(2)}
                </div>
                <div className="text-gray-300 text-sm sm:text-sm mb-2 sm:mb-3">Successfully Withdrawn</div>
                <div className="text-sm text-white">
                  Transactions: {affiliateData.withdrawalHistory.filter(w => w.status === 'completed').length}
                </div>
              </div>
            </div>

            {/* Referral Network Summary */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
              <div className="bg-gray-800 rounded-lg border border-gray-700 p-4 sm:p-6">
                <div className="flex items-center gap-2 sm:gap-3 mb-3 sm:mb-4">
                  <span className="text-xl sm:text-2xl">👥</span>
                  <div>
                    <h3 className="text-base sm:text-lg font-bold text-white">Referral Network</h3>
                    <p className="text-white text-sm sm:text-sm">People you've referred</p>
                  </div>
                </div>
                <div className="text-2xl sm:text-3xl font-bold text-blue-400 mb-2">
                  {affiliateData.referralCount}
                </div>
                <div className="text-gray-300 text-sm sm:text-sm">Total Referrals</div>
              </div>

              <div className="bg-gray-800 rounded-lg border border-gray-700 p-4 sm:p-6">
                <div className="flex items-center gap-2 sm:gap-3 mb-3 sm:mb-4">
                  <span className="text-xl sm:text-2xl">💎</span>
                  <div>
                    <h3 className="text-base sm:text-lg font-bold text-white">Total Portfolio Value</h3>
                    <p className="text-white text-sm sm:text-sm">Current USDT + share value</p>
                  </div>
                </div>
                <div className="text-2xl sm:text-3xl font-bold text-purple-400 mb-2">
                  ${(
                    clientDisplayBalance +
                    ((affiliateData.commissionBalance.total_shares_owned || 0) * parseFloat(affiliateData.currentPhase?.price_per_share || '5'))
                  ).toFixed(2)}
                </div>
                <div className="text-gray-300 text-sm sm:text-sm mb-2 sm:mb-3">Available Balance + Total Shares Value</div>

                {/* Portfolio Value Calculation Explanation */}
                <div className="bg-gray-700/30 rounded-lg p-3 border-l-4 border-purple-400">
                  <h4 className="text-white font-medium text-sm mb-2">💡 How This Is Calculated:</h4>
                  <div className="space-y-1 text-sm text-gray-300">
                    <div className="flex justify-between">
                      <span>Available Balance:</span>
                      <span className="text-green-400">${clientDisplayBalance.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Purchased Shares ({(affiliateData.commissionBalance.purchased_shares || 0).toFixed(2)}):</span>
                      <span className="text-blue-400">${((affiliateData.commissionBalance.purchased_shares || 0) * parseFloat(affiliateData.currentPhase?.price_per_share || '5')).toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Presale Bonus ({(affiliateData.commissionBalance.total_earned_shares || 0).toFixed(2)}):</span>
                      <span className="text-yellow-400">${((affiliateData.commissionBalance.total_earned_shares || 0) * parseFloat(affiliateData.currentPhase?.price_per_share || '10')).toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Converted Shares ({(affiliateData.commissionBalance.total_converted_shares || 0).toFixed(2)}):</span>
                      <span className="text-cyan-400">${((affiliateData.commissionBalance.total_converted_shares || 0) * parseFloat(affiliateData.currentPhase?.price_per_share || '5')).toFixed(2)}</span>
                    </div>
                    <div className="border-t border-gray-600 pt-1 mt-2">
                      <div className="flex justify-between font-medium">
                        <span>Total Portfolio Value:</span>
                        <span className="text-purple-400">${(
                          clientDisplayBalance +
                          ((affiliateData.commissionBalance.total_shares_owned || 0) * parseFloat(affiliateData.currentPhase?.price_per_share || '5'))
                        ).toFixed(2)}</span>
                      </div>
                    </div>
                    <div className="text-sm text-white mt-2">
                      * Share values calculated at current phase price: ${parseFloat(affiliateData.currentPhase?.price_per_share || '5').toFixed(2)} per share
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'transactions' && (
          <div className="space-y-6">
            {/* Transaction Type Tabs */}
            <div className="bg-gray-800 rounded-lg border border-gray-700 p-1">
              <div className="flex space-x-1">
                {[
                  { id: 'usdt', label: '💰 USDT Commissions', icon: '💰' },
                  { id: 'shares', label: '📊 Commission Shares', icon: '📊' },
                  { id: 'purchased', label: '🛒 Purchased Shares', icon: '🛒' },
                  { id: 'conversions', label: '🔄 Conversions', icon: '🔄' },
                  { id: 'withdrawals', label: '💸 Withdrawals', icon: '💸' }
                ].map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTransactionTab(tab.id as any)}
                    className={`flex-1 px-4 py-3 rounded-lg text-sm font-medium transition-colors ${
                      activeTransactionTab === tab.id
                        ? 'bg-yellow-600 text-black'
                        : 'text-gray-300 hover:text-white hover:bg-gray-700'
                    }`}
                  >
                    <span className="mr-2">{tab.icon}</span>
                    {tab.label.replace(/^.* /, '')}
                  </button>
                ))}
              </div>
            </div>

            {/* Transaction Content */}
            <div className="min-h-[400px]">
              {/* USDT Commission Earnings */}
              {activeTransactionTab === 'usdt' && (
        <div className="bg-gray-800 rounded-lg border border-gray-700 p-6">
                  <div className="flex items-center gap-3 mb-6">
                    <span className="text-3xl">💰</span>
                    <div>
                      <h2 className="text-xl font-bold text-white">USDT Commission Earnings</h2>
                      <p className="text-white text-sm">Cash earnings from referral commissions available for withdrawal or conversion to shares</p>
                    </div>
                  </div>

              <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-6">
                <div className="bg-gray-700/50 rounded-lg p-4 text-center">
                  <div className="text-2xl font-bold text-green-400 mb-1">
                    ${clientDisplayBalance.toFixed(2)}
                  </div>
                  <div className="text-gray-300 text-sm">Available Balance</div>
                </div>
                <div className="bg-gray-700/50 rounded-lg p-4 text-center">
                  <div className="text-2xl font-bold text-blue-400 mb-1">
                    ${affiliateData.commissionBalance.total_earned_usdt.toFixed(2)}
                  </div>
                  <div className="text-gray-300 text-sm">Total Earned</div>
                </div>
                <div className="bg-gray-700/50 rounded-lg p-4 text-center">
                  <div className="text-2xl font-bold text-red-400 mb-1">
                    ${affiliateData.commissionBalance.total_withdrawn.toFixed(2)}
                  </div>
                  <div className="text-gray-300 text-sm">Total Withdrawn</div>
                </div>
                <div className="bg-gray-700/50 rounded-lg p-4 text-center">
                  <div className="text-2xl font-bold text-orange-400 mb-1">
                    ${totalWithdrawalFees.toFixed(2)}
                  </div>
                  <div className="text-gray-300 text-sm">Withdrawal Fees</div>
                  <div className="text-gray-500 text-xs mt-1">${withdrawalNetworkFee.toFixed(2)} × {completedWithdrawalCount}</div>
                </div>
                <div className="bg-gray-700/50 rounded-lg p-4 text-center">
                  <div className="text-2xl font-bold text-purple-400 mb-1">
                    ${(affiliateData.commissionBalance.total_usdt_converted || 0).toFixed(2)}
                  </div>
                  <div className="text-gray-300 text-sm">Converted to Shares</div>
                </div>
              </div>

              {/* Recent USDT Commission Transactions */}
              <div className="border-t border-gray-700 pt-4">
                <h4 className="text-white font-medium mb-3">Recent USDT Commission Earnings</h4>
                <div className="space-y-2">
                  {affiliateData.recentTransactions && affiliateData.recentTransactions.filter(t => t.usdt_commission > 0).length > 0 ? (
                    <>
                      {affiliateData.recentTransactions
                        .filter(t => t.usdt_commission > 0)
                        .slice(0, visibleItems.usdtTransactions)
                        .map((transaction) => (
                        <div key={`usdt-${transaction.id}`} className="flex justify-between items-center p-3 bg-gray-700/50 rounded-lg hover:bg-gray-700/70 transition-colors">
                          <div className="flex items-center gap-3">
                            <div className="text-lg">💰</div>
                            <div>
                              <p className="text-white font-medium text-sm">
                                {transaction.transaction_type === 'competition_prize' ? '🏆 Competition Prize'
                                  : transaction.transaction_type === 'performance_pool_claim' ? '⛏️ Gold Diggers Pool Claim'
                                  : transaction.transaction_type === 'performance_pool_payout' ? '⛏️ Gold Diggers Pool Payout'
                                  : transaction.transaction_type === 'performance_pool_share_payout' ? '⛏️ Gold Diggers Share Payout'
                                  : transaction.source_type === 'conversion_user' ? 'Referral Commission (from conversion)'
                                  : 'Commission from Referral Purchase'}
                              </p>
                              <p className="text-sm text-white">
                                {transaction.referred_user ? (
                                  <>
                                    <span className="text-blue-400">👤 {transaction.referred_user.full_name || `${transaction.referred_user.first_name || ''} ${transaction.referred_user.last_name || ''}`.trim() || transaction.referred_user.username}</span>
                                    <span className="text-gray-500"> (@{transaction.referred_user.username})</span>
                                    <span className="text-white"> • ID: {transaction.referred_id}</span>
                                  </>
                                ) : (
                                  <span>Referral ID: {transaction.referred_id || 'N/A'}</span>
                                )} • {new Date(transaction.payment_date || transaction.created_at).toLocaleDateString('en-US', {
                                  month: 'short',
                                  day: 'numeric',
                                  year: 'numeric',
                                  hour: '2-digit',
                                  minute: '2-digit'
                                })}
                              </p>
                              <p className="text-sm text-gray-500">
                                {transaction.transaction_type?.startsWith('performance_pool')
                                  ? (transaction.admin_notes?.match(/Rank #\d+.*?for \d{4}-\d{2}-\d{2}/)?.[0] || 'Performance Pool')
                                  : transaction.source_type === 'conversion_user'
                                  ? `From $${(transaction.share_purchase_amount || 0).toFixed(2)} commission conversion`
                                  : `From $${(transaction.share_purchase_amount || 0).toFixed(2)} purchase`}
                              </p>
                            </div>
                          </div>
                          <div className="text-right">
                            <p className="font-bold text-sm text-green-400">
                              +${(transaction.usdt_commission || 0).toFixed(2)} USDT
                            </p>
                          </div>
                        </div>
                      ))}

                      {/* Load More Button */}
                      {affiliateData.recentTransactions.filter(t => t.usdt_commission > 0).length > visibleItems.usdtTransactions && (
                        <button
                          onClick={() => setVisibleItems(prev => ({ ...prev, usdtTransactions: prev.usdtTransactions + 10 }))}
                          className="w-full mt-4 bg-gray-700 hover:bg-gray-600 text-white font-medium py-2 px-4 rounded-lg transition-colors"
                        >
                          Load More ({affiliateData.recentTransactions.filter(t => t.usdt_commission > 0).length - visibleItems.usdtTransactions} more)
                        </button>
                      )}
                    </>
                  ) : (
                    <div className="text-center py-8">
                      <div className="text-4xl mb-4">💰</div>
                      <p className="text-white mb-2">No USDT commission transactions yet</p>
                      <p className="text-sm text-gray-500">Commission transactions will appear here when your referrals make purchases</p>
                    </div>
                  )}
                </div>
              </div>
                </div>
              )}

              {/* Referral Commission Shares Tab */}
              {activeTransactionTab === 'shares' && (
                <div className="bg-gray-800 rounded-lg border border-gray-700 p-6">
                  <div className="flex items-center gap-3 mb-6">
                    <span className="text-3xl">📊</span>
                    <div>
                      <h2 className="text-xl font-bold text-white">Referral Commission Shares</h2>
                      <p className="text-white text-sm">Free shares earned through the referral program (5% share commission from referral purchases)</p>
                    </div>
                  </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                <div className="bg-gray-700/50 rounded-lg p-4 text-center">
                  <div className="text-2xl font-bold text-yellow-400 mb-1">
                    {affiliateData.commissionBalance.share_balance.toFixed(2)}
                  </div>
                  <div className="text-gray-300 text-sm">Total Commission Shares</div>
                </div>
                <div className="bg-gray-700/50 rounded-lg p-4 text-center">
                  <div className="text-2xl font-bold text-green-400 mb-1">
                    ${(affiliateData.commissionBalance.share_balance * parseFloat(affiliateData.currentPhase?.price_per_share || '5')).toFixed(2)}
                  </div>
                  <div className="text-gray-300 text-sm">Current Value</div>
                </div>
                {DIVIDENDS_USER_VISIBLE && (
                <div className="bg-gray-700/50 rounded-lg p-4 text-center">
                  <div className="text-2xl font-bold text-purple-400 mb-1">
                    ${(affiliateData.commissionBalance.share_balance * 0.5).toFixed(2)}
                  </div>
                  <div className="text-gray-300 text-sm">Annual Dividend Projection</div>
                </div>
                )}
              </div>

              {/* Recent Commission Share Transactions */}
              <div className="border-t border-gray-700 pt-4">
                <h4 className="text-white font-medium mb-3">Recent Commission Share Awards</h4>
                <div className="space-y-2">
                  {affiliateData.recentTransactions && affiliateData.recentTransactions.filter(t => t.share_commission > 0).length > 0 ? (
                    <>
                      {affiliateData.recentTransactions
                        .filter(t => t.share_commission > 0)
                        .slice(0, visibleItems.shareTransactions)
                        .map((transaction) => (
                        <div key={`shares-${transaction.id}`} className="flex justify-between items-center p-3 bg-gray-700/50 rounded-lg hover:bg-gray-700/70 transition-colors">
                          <div className="flex items-center gap-3">
                            <div className="text-lg">📊</div>
                            <div>
                              <p className="text-white font-medium text-sm">
                                {transaction.source_type === 'conversion_user'
                                  ? 'Commission Shares (from conversion)'
                                  : 'Commission Shares from Referral'}
                              </p>
                              <p className="text-sm text-white">
                                {transaction.referred_user ? (
                                  <>
                                    <span className="text-blue-400">👤 {transaction.referred_user.full_name || `${transaction.referred_user.first_name || ''} ${transaction.referred_user.last_name || ''}`.trim() || transaction.referred_user.username}</span>
                                    <span className="text-gray-500"> (@{transaction.referred_user.username})</span>
                                    <span className="text-white"> • ID: {transaction.referred_id}</span>
                                  </>
                                ) : (
                                  <span>Referral ID: {transaction.referred_id || 'N/A'}</span>
                                )} • {new Date(transaction.payment_date || transaction.created_at).toLocaleDateString('en-US', {
                                  month: 'short',
                                  day: 'numeric',
                                  year: 'numeric',
                                  hour: '2-digit',
                                  minute: '2-digit'
                                })}
                              </p>
                              <p className="text-sm text-gray-500">
                                {transaction.source_type === 'conversion_user'
                                  ? `From $${(transaction.share_purchase_amount || 0).toFixed(2)} commission conversion`
                                  : `From $${(transaction.share_purchase_amount || 0).toFixed(2)} purchase`}
                              </p>
                            </div>
                          </div>
                          <div className="text-right">
                            <p className="font-bold text-sm text-yellow-400">
                              +{(transaction.share_commission || 0).toFixed(2)} Shares
                            </p>
                          </div>
                        </div>
                      ))}

                      {/* Load More Button */}
                      {affiliateData.recentTransactions.filter(t => t.share_commission > 0).length > visibleItems.shareTransactions && (
                        <button
                          onClick={() => setVisibleItems(prev => ({ ...prev, shareTransactions: prev.shareTransactions + 10 }))}
                          className="w-full mt-4 bg-gray-700 hover:bg-gray-600 text-white font-medium py-2 px-4 rounded-lg transition-colors"
                        >
                          Load More ({affiliateData.recentTransactions.filter(t => t.share_commission > 0).length - visibleItems.shareTransactions} more)
                        </button>
                      )}
                    </>
                  ) : (
                    <div className="text-center py-8">
                      <div className="text-4xl mb-4">📊</div>
                      <p className="text-white mb-2">No commission share transactions yet</p>
                      <p className="text-sm text-gray-500">Commission share awards will appear here when your referrals make purchases</p>
                    </div>
                  )}
                </div>
              </div>
                </div>
              )}

              {/* Purchased Shares Tab */}
              {activeTransactionTab === 'purchased' && (
                <div className="bg-gray-800 rounded-lg border border-gray-700 p-6">
                  <div className="flex items-center gap-3 mb-6">
                    <span className="text-3xl">🛒</span>
                    <div>
                      <h2 className="text-xl font-bold text-white">Purchased Shares</h2>
                      <p className="text-white text-sm">Direct share purchases with your own funds</p>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                    <div className="bg-gray-700/50 rounded-lg p-4 text-center">
                      <div className="text-2xl font-bold text-blue-400 mb-1">
                        {(affiliateData.commissionBalance.purchased_shares || 0).toFixed(2)}
                      </div>
                      <div className="text-gray-300 text-sm">Shares Purchased</div>
                    </div>
                    <div className="bg-gray-700/50 rounded-lg p-4 text-center">
                      <div className="text-2xl font-bold text-green-400 mb-1">
                        ${(affiliateData.commissionBalance.purchased_amount || 0).toFixed(2)}
                      </div>
                      <div className="text-gray-300 text-sm">Amount Paid</div>
                    </div>
                    <div className="bg-gray-700/50 rounded-lg p-4 text-center">
                      <div className="text-2xl font-bold text-purple-400 mb-1">
                        ${((affiliateData.commissionBalance.purchased_shares || 0) * parseFloat(affiliateData.currentPhase?.price_per_share || '5')).toFixed(2)}
                      </div>
                      <div className="text-gray-300 text-sm">Current Value</div>
                    </div>
                  </div>

                  {/* Purchase History */}
                  <div className="space-y-3">
                    <h3 className="text-lg font-semibold text-white mb-4">Purchase History</h3>
                    {affiliateData.purchaseHistory && affiliateData.purchaseHistory.length > 0 ? (
                      affiliateData.purchaseHistory
                        .filter(purchase => purchase.payment_method !== 'Commission Conversion')
                        .map((purchase, index) => (
                        <div key={index} className="bg-gray-700/30 rounded-lg p-4 border-l-4 border-blue-400">
                          <div className="flex justify-between items-start mb-2">
                            <div>
                              <div className="text-white font-medium">
                                {purchase.shares_purchased} shares purchased
                              </div>
                              <div className="text-white text-sm">
                                {new Date(purchase.created_at).toLocaleDateString()} • {purchase.payment_method}
                              </div>
                            </div>
                            <div className="text-right">
                              <div className="text-green-400 font-bold">
                                ${parseFloat(purchase.total_amount).toFixed(2)}
                              </div>
                              <div className="text-white text-sm">
                                ${(parseFloat(purchase.total_amount) / purchase.shares_purchased).toFixed(2)}/share
                              </div>
                            </div>
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className="text-center py-12">
                        <div className="text-4xl mb-4">🛒</div>
                        <p className="text-white mb-2">No share purchases yet</p>
                        <p className="text-sm text-gray-500">Your direct share purchases will appear here</p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Conversions Tab */}
              {activeTransactionTab === 'conversions' && (
                <div className="bg-gray-800 rounded-lg border border-gray-700 p-6">
                  <div className="flex items-center gap-3 mb-6">
                    <span className="text-3xl">🔄</span>
                    <div>
                      <h2 className="text-xl font-bold text-white">Commission Conversions</h2>
                      <p className="text-white text-sm">USDT commissions converted to shares</p>
                    </div>
                  </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                <div className="bg-gray-700/50 rounded-lg p-4 text-center">
                  <div className="text-2xl font-bold text-blue-400 mb-1">
                    {(affiliateData.commissionBalance.total_converted_shares || 0).toFixed(2)}
                  </div>
                  <div className="text-gray-300 text-sm">Shares Purchased</div>
                </div>
                <div className="bg-gray-700/50 rounded-lg p-4 text-center">
                  <div className="text-2xl font-bold text-purple-400 mb-1">
                    ${(affiliateData.commissionBalance.total_usdt_converted || 0).toFixed(2)}
                  </div>
                  <div className="text-gray-300 text-sm">USDT Converted</div>
                </div>
                <div className="bg-gray-700/50 rounded-lg p-4 text-center">
                  <div className="text-2xl font-bold text-green-400 mb-1">
                    ${((affiliateData.commissionBalance.total_converted_shares || 0) * parseFloat(affiliateData.currentPhase?.price_per_share || '5')).toFixed(2)}
                  </div>
                  <div className="text-gray-300 text-sm">Current Value</div>
                </div>
              </div>

              <div className="flex flex-wrap gap-3 mb-6">
                <button
                  onClick={() => setActiveTab('convert')}
                  className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg transition-colors font-medium hover:transform hover:scale-105"
                >
                  Convert USDT to Shares
                </button>
                <button
                  onClick={() => setActiveTab('history')}
                  className="bg-gray-600 hover:bg-gray-700 text-white px-6 py-2 rounded-lg transition-colors font-medium hover:transform hover:scale-105"
                >
                  View Conversion History
                </button>
              </div>

              {/* Recent Conversion Transactions */}
              <div className="border-t border-gray-700 pt-4">
                <h4 className="text-white font-medium mb-3">Recent Commission Conversions</h4>
                <div className="space-y-2">
                  {affiliateData.conversionHistory && affiliateData.conversionHistory.filter(c => c.status === 'approved').length > 0 ? (
                    <>
                      {affiliateData.conversionHistory
                        .filter(c => c.status === 'approved')
                        .slice(0, visibleItems.conversions)
                        .map((conversion) => (
                        <div key={`conversion-${conversion.id}`} className="flex justify-between items-center p-3 bg-gray-700/50 rounded-lg hover:bg-gray-700/70 transition-colors">
                          <div className="flex items-center gap-3">
                            <div className="text-lg">🔄</div>
                            <div>
                              <p className="text-white font-medium text-sm">USDT → Shares Conversion</p>
                              <p className="text-sm text-white">
                                Conversion ID: {conversion.id} • {new Date(conversion.approved_at || conversion.created_at).toLocaleDateString('en-US', {
                                  month: 'short',
                                  day: 'numeric',
                                  year: 'numeric',
                                  hour: '2-digit',
                                  minute: '2-digit'
                                })}
                              </p>
                              <p className="text-sm text-gray-500">
                                ${parseFloat(conversion.usdt_amount || 0).toFixed(2)} USDT converted
                              </p>
                            </div>
                          </div>
                          <div className="text-right">
                            <p className="font-bold text-sm text-blue-400">
                              +{getConversionShares(conversion).toFixed(2)} Shares
                            </p>
                            <span className="px-2 py-1 rounded-full text-sm font-medium bg-green-900/30 text-green-300">
                              APPROVED
                            </span>
                          </div>
                        </div>
                      ))}

                      {/* Load More Button */}
                      {affiliateData.conversionHistory.filter(c => c.status === 'approved').length > visibleItems.conversions && (
                        <button
                          onClick={() => setVisibleItems(prev => ({ ...prev, conversions: prev.conversions + 10 }))}
                          className="w-full mt-4 bg-gray-700 hover:bg-gray-600 text-white font-medium py-2 px-4 rounded-lg transition-colors"
                        >
                          Load More ({affiliateData.conversionHistory.filter(c => c.status === 'approved').length - visibleItems.conversions} more)
                        </button>
                      )}
                    </>
                  ) : (
                    <div className="text-center py-8">
                      <div className="text-4xl mb-4">🔄</div>
                      <p className="text-white mb-2">No conversion transactions yet</p>
                      <p className="text-sm text-gray-500">USDT to shares conversions will appear here when you convert your commissions</p>
                    </div>
                  )}
                </div>
              </div>
                </div>
              )}

              {/* Withdrawals Tab */}
              {activeTransactionTab === 'withdrawals' && (
                <div className="bg-gray-800 rounded-lg border border-gray-700 p-6">
                  <div className="flex items-center gap-3 mb-6">
                    <span className="text-3xl">💸</span>
                    <div>
                      <h2 className="text-xl font-bold text-white">Withdrawal History</h2>
                      <p className="text-white text-sm">Track all your USDT withdrawal requests and their status</p>
                    </div>
                  </div>

              {affiliateData.withdrawalHistory.length > 0 ? (
                <div className="space-y-3">
                  {affiliateData.withdrawalHistory.map((withdrawal) => (
                    <div key={withdrawal.id} className="bg-gray-700/50 rounded-lg p-4">
                      <div className="flex justify-between items-start mb-2">
                        <div className="flex items-center gap-3">
                          <div className="text-lg">💸</div>
                          <div>
                            <p className="text-white font-medium">
                              Withdrawal Request #{withdrawal.id}
                            </p>
                            <p className="text-sm text-white">
                              {new Date(withdrawal.created_at).toLocaleDateString('en-US', {
                                month: 'short',
                                day: 'numeric',
                                year: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit'
                              })}
                            </p>
                            <p className="text-sm text-gray-500">
                              Amount: ${parseFloat(withdrawal.withdrawal_amount).toFixed(2)} USDT
                            </p>
                          </div>
                        </div>
                        <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                          withdrawal.status === 'completed'
                            ? 'bg-green-900/30 text-green-300'
                            : withdrawal.status === 'pending'
                            ? 'bg-yellow-900/30 text-yellow-300'
                            : withdrawal.status === 'processing'
                            ? 'bg-blue-900/30 text-blue-300'
                            : 'bg-red-900/30 text-red-300'
                        }`}>
                          {withdrawal.status.toUpperCase()}
                        </span>
                      </div>

                      {withdrawal.wallet_address && (
                        <div className="text-sm text-white mb-1">
                          <span className="font-medium">Wallet:</span> {withdrawal.wallet_address.slice(0, 10)}...{withdrawal.wallet_address.slice(-6)}
                        </div>
                      )}

                      {withdrawal.network && (
                        <div className="text-sm text-white mb-1">
                          <span className="font-medium">Network:</span> {withdrawal.network}
                        </div>
                      )}

                      {withdrawal.admin_notes && (
                        <div className="text-sm text-gray-300 mt-2 p-2 bg-gray-600/30 rounded">
                          <span className="font-medium">Admin Notes:</span> {withdrawal.admin_notes}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <div className="text-4xl mb-4">💸</div>
                  <p className="text-white mb-4">No withdrawal history yet</p>
                  <button
                    onClick={() => setActiveTab('withdraw')}
                    className="bg-green-600 hover:bg-green-700 text-white px-6 py-2 rounded-lg transition-colors"
                  >
                    Make Your First Withdrawal
                  </button>
                </div>
              )}
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === 'convert' && (
          <div className="bg-gray-800 rounded-lg border border-gray-700 p-6">
            <h3 className="text-xl font-bold text-white mb-6">🔄 Convert USDT to Shares</h3>

            {/* Operation Status Messages */}
            {operationSuccess && (
              <div className="mb-6 bg-green-900/20 border border-green-500/30 rounded-lg p-4">
                <div className="flex items-center space-x-3">
                  <div className="text-2xl">✅</div>
                  <p className="text-green-300">{operationSuccess}</p>
                </div>
              </div>
            )}

            {operationError && (
              <div className="mb-6 bg-red-900/20 border border-red-500/30 rounded-lg p-4">
                <div className="flex items-center space-x-3">
                  <div className="text-2xl">❌</div>
                  <p className="text-red-300">{operationError}</p>
                </div>
              </div>
            )}

            {/* Current Balances */}
            {(() => {
              return (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
              <div className="bg-gray-700 rounded-lg p-4">
                <h4 className="text-sm font-medium text-gray-300 mb-1">Available Balance</h4>
                <p className="text-2xl font-bold text-green-400">
                  ${rawUsdtBalance.toFixed(2)}
                </p>
              </div>
              <div className="bg-gray-700 rounded-lg p-4">
                <h4 className="text-sm font-medium text-gray-300 mb-1">Current Share Price</h4>
                <p className="text-2xl font-bold text-yellow-400">
                  ${parseFloat(affiliateData.currentPhase.price_per_share).toFixed(2)}
                </p>
                <p className="text-sm text-white">{affiliateData.currentPhase.phase_name}</p>
              </div>
            </div>
              );
            })()}

            {/* Top-up CTA: shown when the 1-share cap (delta minus pending top-ups) is positive (gated to allow-listed users). KYC NOT required: top-up only funds the commission balance. */}
            {isTopupAllowed && !isProductionCapitalDateActive() && (() => {
              const showCta = topupCap.maxAllowed > 0;
              return showCta ? (
                <div className="bg-blue-900/20 border border-blue-500/30 rounded-lg p-4 mb-4">
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div>
                      <h4 className="font-semibold text-white mb-1">Need more USDT to buy 1 share?</h4>
                      <p className="text-sm text-blue-200">
                        You can top up exactly ${topupCap.maxAllowed.toFixed(2)} to reach 1 share at ${topupCap.sharePrice.toFixed(2)}
                        {topupCap.pendingTotal > 0 ? ` (accounting for $${topupCap.pendingTotal.toFixed(2)} in pending top-ups)` : ''}.
                        Send USDT externally and submit the TXID below for admin approval.
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={openTopupModal}
                      className="bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-lg whitespace-nowrap"
                    >Top-up Balance</button>
                  </div>
                </div>
              ) : null;
            })()}

            {/* Pending top-up history (gated to allow-listed users) */}
            {isTopupAllowed && pendingTopups.length > 0 && (
              <div className="bg-gray-700/50 border border-gray-600 rounded-lg p-4 mb-4">
                <h4 className="text-white font-semibold mb-2 text-sm">Recent Top-up Requests</h4>
                <div className="space-y-2">
                  {pendingTopups.slice(0, 5).map(t => {
                    const amount = parseFloat(String(t.topup_payment_amount || '0'));
                    const color = t.status === 'approved' ? 'text-green-400' : t.status === 'rejected' ? 'text-red-400' : 'text-yellow-400';
                    return (
                      <div key={t.id} className="flex items-center justify-between text-xs">
                        <span className="text-gray-300 font-mono">{t.transaction_reference || t.id.slice(0, 8)}</span>
                        <span className="text-white">${amount.toFixed(2)}</span>
                        <span className={color}>{t.status.toUpperCase()}</span>
                        <span className="text-gray-400">{new Date(t.created_at).toLocaleDateString()}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Conversion Calculator */}
            {isProductionCapitalDateActive() ? (
              <CommissionPackageChooser
                balance={rawUsdtBalance}
                pendingTopups={topupCap.pendingTotal}
                phasePrice={parseFloat(affiliateData.currentPhase.price_per_share)}
                disabled={conversionLoading}
                submitLabel={conversionLoading ? 'Processing Conversion...' : 'Convert packages to shares'}
                onConvert={handlePackageConversion}
                onTopUp={openPackageTopup}
              />
            ) : (
            <form onSubmit={handleConversionSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  USDT Amount to Convert
                </label>
                <input
                  type="number"
                  step="0.01"
                  min={parseFloat(affiliateData.currentPhase.price_per_share)}
                  max={rawUsdtBalance}
                  value={conversionForm.usdtAmount}
                  onChange={(e) => setConversionForm(prev => ({ ...prev, usdtAmount: e.target.value }))}
                  className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-yellow-500"
                  placeholder={`Enter USDT amount (minimum $${parseFloat(affiliateData.currentPhase.price_per_share).toFixed(2)} for 1 share)`}
                />
              </div>

              {/* Conversion Preview */}
              {conversionForm.usdtAmount && parseFloat(conversionForm.usdtAmount) > 0 && (
                <div className="bg-gray-700 rounded-lg p-4">
                  <h4 className="text-sm font-medium text-gray-300 mb-2">Conversion Preview</h4>
                  <div className="flex justify-between items-center">
                    <span className="text-white">You will receive:</span>
                    <span className="text-yellow-400 font-bold">
                      {Math.floor(parseFloat(conversionForm.usdtAmount) / parseFloat(affiliateData.currentPhase.price_per_share))} shares
                    </span>
                  </div>
                  {parseFloat(conversionForm.usdtAmount) < parseFloat(affiliateData.currentPhase.price_per_share) && (
                    <p className="text-red-400 text-sm mt-2">
                      ⚠️ Minimum ${parseFloat(affiliateData.currentPhase.price_per_share).toFixed(2)} required for 1 share
                    </p>
                  )}
                </div>
              )}

              {(() => {
                const sharePrice = parseFloat(affiliateData.currentPhase.price_per_share);
                const availableBalance = rawUsdtBalance;
                const enteredAmount = parseFloat(conversionForm.usdtAmount) || 0;
                const canAffordOneShare = availableBalance >= sharePrice;
                const enteredEnough = enteredAmount >= sharePrice;

                if (!canAffordOneShare) {
                  return (
                    <button
                      type="button"
                      disabled
                      className="w-full bg-gray-600 cursor-not-allowed text-white font-bold py-3 px-4 rounded-lg"
                    >
                      Not Enough to Convert (need ${sharePrice.toFixed(2)} min)
                    </button>
                  );
                }

                return (
                  <button
                    type="submit"
                    disabled={conversionLoading || !conversionForm.usdtAmount || !enteredEnough}
                    className="w-full bg-yellow-600 hover:bg-yellow-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-black font-bold py-3 px-4 rounded-lg transition-colors"
                  >
                    {conversionLoading ? (
                      <div className="flex items-center justify-center">
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-black mr-2"></div>
                        Processing Conversion...
                      </div>
                    ) : (
                      'Convert USDT to Shares'
                    )}
                  </button>
                );
              })()}
            </form>
            )}

            {/* Top-up Modal (gated to allow-listed users) */}
            {isTopupAllowed && showTopupModal && (
              <div className="fixed inset-0 bg-black bg-opacity-50 flex items-start justify-center z-50 p-4 pt-8 overflow-y-auto">
                <div className="bg-gray-800 rounded-lg border border-gray-700 p-6 w-full max-w-lg mb-8">
                  <div className="flex items-start justify-between mb-4">
                    <h3 className="text-xl font-bold text-white">💵 Top-up Commission Balance</h3>
                    <button onClick={() => setShowTopupModal(false)} className="text-gray-400 hover:text-white text-2xl leading-none">×</button>
                  </div>
                  <p className="text-sm text-gray-300 mb-4">
                    Send USDT to one of the company wallets below, then submit the transaction details for admin approval. Once approved, your commission balance will be credited and you can use the converter above to buy shares.
                  </p>

                  {topupWallets.length === 0 ? (
                    <div className="bg-yellow-900/20 border border-yellow-500/30 rounded p-3 mb-4 text-yellow-300 text-sm">No company wallets are currently configured. Please contact support.</div>
                  ) : (
                    <>
                      <label className="block text-xs font-medium text-gray-300 mb-2">Select USDT Network</label>
                      <div className="grid gap-2 mb-4">
                        {topupWallets.map(w => {
                          const isSelected = topupNetwork === w.network;
                          return (
                            <button
                              key={w.id}
                              type="button"
                              onClick={() => setTopupNetwork(w.network)}
                              className={`w-full flex items-center justify-between rounded-lg border p-3 transition-colors ${
                                isSelected
                                  ? 'border-yellow-500 bg-yellow-900/20'
                                  : 'border-gray-600 bg-gray-700 hover:border-yellow-500/60'
                              }`}
                            >
                              <div className="flex items-center gap-3">
                                <span className="text-2xl">{w.icon || '💰'}</span>
                                <div className="text-left">
                                  <div className="font-semibold text-white text-sm">{w.name}</div>
                                  <div className="text-xs text-gray-400">USDT-{w.technical}</div>
                                </div>
                              </div>
                              <div className={`text-sm ${isSelected ? 'text-yellow-400' : 'text-gray-500'}`}>{isSelected ? '✓' : '→'}</div>
                            </button>
                          );
                        })}
                      </div>
                      {(() => {
                        const w = topupWallets.find(x => x.network === topupNetwork);
                        return w ? (
                          <div className="bg-gray-700/60 border border-gray-600 rounded p-3 mb-4">
                            <div className="text-xs text-gray-400 mb-1">Send USDT-{w.technical} to:</div>
                            <div className="font-mono text-sm text-yellow-300 break-all">{w.wallet_address}</div>
                            <button
                              type="button"
                              onClick={() => { try { navigator.clipboard.writeText(w.wallet_address); } catch {} }}
                              className="mt-2 text-xs bg-gray-600 hover:bg-gray-500 text-white px-2 py-1 rounded"
                            >Copy address</button>
                            <div className="mt-2 text-[11px] text-yellow-300/80">
                              ⚠️ Only send USDT on the {w.name} ({w.technical}) network. Sending on the wrong network will result in lost funds.
                            </div>
                          </div>
                        ) : null;
                      })()}
                    </>
                  )}

                  <label className="block text-xs font-medium text-gray-300 mb-1">
                    USDT Amount to Send <span className="text-yellow-400 ml-2">(locked)</span>
                  </label>
                  <input
                    type="text"
                    value={`$${(topupLock?.amount ?? topupCap.maxAllowed).toFixed(2)}`}
                    readOnly
                    tabIndex={-1}
                    aria-readonly="true"
                    className="w-full bg-gray-900 border border-gray-700 rounded px-2 py-2 text-yellow-300 text-sm font-mono mb-1 cursor-not-allowed select-all"
                  />
                  <p className="text-xs text-gray-400 mb-3">
                    {topupLock
                      ? `Locked to the exact USDT still needed to buy ${topupLock.label} after your commission balance and pending top-ups.`
                      : `Locked to the exact USDT needed to reach 1 share at $${topupCap.sharePrice.toFixed(2)}${topupCap.currentBalance > 0 ? ` − $${topupCap.currentBalance.toFixed(2)} balance` : ''}${topupCap.pendingTotal > 0 ? ` − $${topupCap.pendingTotal.toFixed(2)} pending` : ''}`}
                    .
                    Send EXACTLY this amount on the selected network.
                  </p>

                  <label className="block text-xs font-medium text-gray-300 mb-1">Transaction Hash (TXID)</label>
                  <input
                    type="text" value={topupTxHash}
                    onChange={(e) => setTopupTxHash(e.target.value)}
                    placeholder="0x… or TRON tx hash"
                    className="w-full bg-gray-700 border border-gray-600 rounded px-2 py-2 text-white text-sm mb-3 font-mono"
                  />

                  <label className="block text-xs font-medium text-gray-300 mb-1">Sender Wallet Address</label>
                  <input
                    type="text" value={topupSenderWallet}
                    onChange={(e) => setTopupSenderWallet(e.target.value)}
                    placeholder="The wallet you sent the USDT from"
                    className="w-full bg-gray-700 border border-gray-600 rounded px-2 py-2 text-white text-sm mb-4 font-mono"
                  />

                  {topupError && <div className="bg-red-900/20 border border-red-500/30 rounded p-2 mb-3 text-red-300 text-sm">{topupError}</div>}
                  {topupSuccess && <div className="bg-green-900/20 border border-green-500/30 rounded p-2 mb-3 text-green-300 text-sm">{topupSuccess}</div>}

                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => setShowTopupModal(false)}
                      disabled={isSubmittingTopup}
                      className="flex-1 bg-gray-600 hover:bg-gray-700 disabled:bg-gray-700 text-white font-medium py-2 px-4 rounded-lg"
                    >Cancel</button>
                    <button
                      type="button"
                      onClick={submitTopup}
                      disabled={
                        isSubmittingTopup
                        || topupWallets.length === 0
                        || topupCap.maxAllowed <= 0
                      }
                      className="flex-1 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white font-medium py-2 px-4 rounded-lg"
                    >{isSubmittingTopup ? 'Submitting…' : 'Submit Top-up'}</button>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* ============================================================================
            SHARE TRANSFERS DISABLED
            ============================================================================
            This section has been disabled due to multiple reports of shareholders
            attempting to sell shares in violation of platform terms. Share transfers
            will remain disabled until all 1,400,000 shares across the 19 phases have
            been sold.
            ============================================================================ */}
        {activeTab === 'transfer' && (
          <div className="bg-gray-800 rounded-lg border border-gray-700 p-4 sm:p-6">
            <h3 className="text-lg sm:text-xl font-bold text-white mb-4 sm:mb-6">📤 Transfer Shares</h3>

            {/* Disabled Notice */}
            <div className="bg-red-900/30 border border-red-500/50 rounded-lg p-4 sm:p-6 mb-6">
              <div className="flex items-start gap-3 sm:gap-4">
                <div className="flex-shrink-0">
                  <svg className="w-6 h-6 sm:w-8 sm:h-8 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                </div>
                <div>
                  <h4 className="text-base sm:text-lg font-bold text-red-400 mb-3">
                    ⚠️ Share Transfers Permanently Disabled
                  </h4>
                  <div className="space-y-3 text-sm sm:text-base text-gray-300">
                    <p>
                      Share transfers have been <strong className="text-red-400">permanently disabled</strong> on this platform.
                    </p>
                    <p>
                      <strong className="text-white">Reason:</strong> Multiple reports have been received of users attempting to sell shares they purchased, which is a direct violation of our platform terms and conditions.
                    </p>
                    <p>
                      This behavior is considered <strong className="text-red-400">market manipulation</strong> and undermines the integrity of the shareholder ecosystem. Such activities are not tolerated.
                    </p>
                    <div className="bg-gray-800/50 rounded-lg p-3 sm:p-4 mt-4 border border-gray-600">
                      <p className="text-sm sm:text-sm text-white">
                        <strong className="text-yellow-400">📌 Note:</strong> Share transfers will remain disabled until all <strong className="text-white">1,400,000 shares</strong> across the <strong className="text-white">19 phases</strong> have been sold.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Your Shares Info (Read-only) */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="bg-gray-700/50 rounded-lg p-4">
                <h4 className="text-sm sm:text-sm font-medium text-white mb-1">Your Shares</h4>
                <p className="text-2xl sm:text-3xl font-bold text-yellow-400">
                  {(affiliateData.commissionBalance.total_shares_owned || 0).toFixed(2)}
                </p>
                <p className="text-sm sm:text-sm text-gray-500">
                  Shares are held in your account and cannot be transferred
                </p>
              </div>
              <div className="bg-gray-700/50 rounded-lg p-4">
                <h4 className="text-sm sm:text-sm font-medium text-white mb-1">Current Share Value</h4>
                <p className="text-2xl sm:text-3xl font-bold text-green-400">
                  ${((affiliateData.commissionBalance.total_shares_owned || 0) * parseFloat(affiliateData.currentPhase.price_per_share)).toFixed(2)}
                </p>
                <p className="text-sm sm:text-sm text-gray-500">
                  At ${parseFloat(affiliateData.currentPhase.price_per_share).toFixed(2)} per share
                </p>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'withdraw' && (
          <div className="bg-gray-800 rounded-lg border border-gray-700 p-4 sm:p-6">
            <h3 className="text-lg sm:text-xl font-bold text-white mb-4 sm:mb-6">💸 Withdraw USDT Commission</h3>

            {/* KYC Status Display */}
            <div className={`rounded-lg p-3 sm:p-4 mb-4 sm:mb-6 ${
              kycValidation?.canWithdraw
                ? 'bg-green-900/20 border border-green-500/30'
                : 'bg-yellow-900/20 border border-yellow-500/30'
            }`}>
              <div className="flex items-start space-x-2 sm:space-x-3">
                <div className="text-xl sm:text-2xl flex-shrink-0">
                  {kycValidation?.canWithdraw ? '✅' : '⚠️'}
                </div>
                <div className="min-w-0 flex-1">
                  <h4 className="font-semibold text-white mb-1 text-sm sm:text-base">
                    {kycValidation?.canWithdraw ? 'KYC Verified' : 'KYC Required'}
                  </h4>
                  <p className={`text-sm sm:text-sm ${
                    kycValidation?.canWithdraw ? 'text-green-300' : 'text-yellow-300'
                  }`}>
                    {kycValidation?.message}
                  </p>
                  {!kycValidation?.canWithdraw && (
                    <button
                      onClick={() => window.location.href = '#kyc'}
                      className="mt-2 bg-yellow-600 hover:bg-yellow-700 text-white text-sm sm:text-sm px-3 py-2 rounded transition-colors"
                    >
                      Complete KYC
                    </button>
                  )}
                </div>
              </div>
            </div>

            {/* Success Message at top */}
            {operationSuccess && (
              <div className="mb-6 bg-green-900/20 border border-green-500/30 rounded-lg p-4">
                <div className="flex items-center space-x-3">
                  <div className="text-2xl">✅</div>
                  <p className="text-green-300">{operationSuccess}</p>
                </div>
              </div>
            )}

            {/* Pending Confirmations Section */}
            {affiliateData.withdrawalHistory.filter(w => w.status === 'approved').length > 0 && (
              <div className="bg-blue-900/20 border border-blue-500/30 rounded-lg p-4 mb-6">
                <div className="flex items-center gap-3 mb-4">
                  <span className="text-2xl">🎉</span>
                  <div>
                    <h4 className="text-lg font-bold text-blue-300">Withdrawal Approved!</h4>
                    <p className="text-sm text-blue-200">Please confirm receipt of your funds</p>
                  </div>
                </div>

                <div className="space-y-3">
                  {affiliateData.withdrawalHistory
                    .filter(w => w.status === 'approved')
                    .map((withdrawal) => (
                      <div key={withdrawal.id} className="bg-gray-700/50 rounded-lg p-4">
                        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-3">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2">
                              <span className="text-lg">💰</span>
                              <p className="text-white font-bold text-lg">
                                ${parseFloat(withdrawal.withdrawal_amount).toFixed(2)} USDT
                              </p>
                            </div>
                            <div className="space-y-1 text-sm">
                              <p className="text-gray-300">
                                <span className="font-medium">Wallet:</span>{' '}
                                <span className="font-mono text-sm">
                                  {withdrawal.wallet_address?.slice(0, 10)}...{withdrawal.wallet_address?.slice(-6)}
                                </span>
                              </p>
                              <p className="text-gray-300">
                                <span className="font-medium">Network:</span> {withdrawal.network}
                              </p>
                              {withdrawal.transaction_hash && (
                                <p className="text-gray-300">
                                  <span className="font-medium">TX Hash:</span>{' '}
                                  <span className="font-mono text-sm">
                                    {withdrawal.transaction_hash.slice(0, 10)}...{withdrawal.transaction_hash.slice(-6)}
                                  </span>
                                </p>
                              )}
                              {withdrawal.proof_of_payment_url && (
                                <a
                                  href={withdrawal.proof_of_payment_url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-blue-400 hover:text-blue-300 text-sm flex items-center gap-1"
                                >
                                  📄 View Proof of Payment
                                </a>
                              )}
                            </div>
                          </div>
                          <button
                            onClick={() => handleWithdrawalConfirmation(withdrawal.id)}
                            disabled={confirmingWithdrawal === withdrawal.id}
                            className="bg-green-600 hover:bg-green-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white font-bold py-2 px-4 rounded-lg transition-colors text-sm whitespace-nowrap"
                          >
                            {confirmingWithdrawal === withdrawal.id ? (
                              <div className="flex items-center gap-2">
                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                                <span>Confirming...</span>
                              </div>
                            ) : (
                              '✅ Confirm Receipt'
                            )}
                          </button>
                        </div>
                      </div>
                    ))}
                </div>

                <div className="mt-4 bg-yellow-900/20 border border-yellow-500/30 rounded-lg p-3">
                  <p className="text-yellow-200 text-sm">
                    ⚠️ <strong>Important:</strong> Only confirm after you have verified the funds are in your wallet.
                    This action cannot be undone.
                  </p>
                </div>
              </div>
            )}

            {/* Pending Verification Withdrawals Section */}
            {affiliateData.withdrawalHistory.filter(w => w.status === 'pending_verification').length > 0 && (
              <div className="bg-yellow-900/20 border border-yellow-500/50 rounded-lg p-4 sm:p-6 mb-4 sm:mb-6">
                <div className="flex items-center space-x-3 mb-4">
                  <div className="text-xl sm:text-2xl">⏳</div>
                  <div>
                    <h4 className="text-base sm:text-lg font-bold text-yellow-400">Pending Verification</h4>
                    <p className="text-yellow-300 text-sm sm:text-sm">
                      The following withdrawal(s) are awaiting your email verification.
                    </p>
                  </div>
                </div>

                <div className="space-y-4">
                  {affiliateData.withdrawalHistory
                    .filter(w => w.status === 'pending_verification')
                    .map((withdrawal) => (
                      <div key={withdrawal.id} className="bg-gray-800 rounded-lg p-3 sm:p-4 border border-yellow-500/30">
                        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-2 mb-3">
                          <div>
                            <div className="text-yellow-400 font-semibold text-base sm:text-lg">
                              ${parseFloat(withdrawal.withdrawal_amount).toFixed(2)} {withdrawal.currency || 'USDT'}
                            </div>
                            <div className="text-white text-sm sm:text-sm">
                              Requested: {new Date(withdrawal.created_at).toLocaleDateString()} at {new Date(withdrawal.created_at).toLocaleTimeString()}
                            </div>
                          </div>
                          <span className="px-2 sm:px-3 py-1 rounded-full text-[10px] sm:text-sm font-medium bg-yellow-900/50 text-yellow-400 border border-yellow-500/50 self-start">
                            AWAITING VERIFICATION
                          </span>
                        </div>

                        {withdrawal.wallet_address && (
                          <div className="text-white text-sm sm:text-sm mb-2">
                            <span className="font-medium">Wallet:</span>{' '}
                            <span className="font-mono text-gray-300 break-all">
                              {withdrawal.wallet_address.substring(0, 10)}...{withdrawal.wallet_address.slice(-6)}
                            </span>
                          </div>
                        )}

                        {withdrawal.network && (
                          <div className="text-white text-sm sm:text-sm mb-3">
                            <span className="font-medium">Network:</span> {withdrawal.network}
                          </div>
                        )}

                        <div className="bg-yellow-900/30 border border-yellow-500/30 rounded p-2 sm:p-3 mb-3 sm:mb-4">
                          <p className="text-yellow-300 text-sm sm:text-sm">
                            📧 A verification link was sent to your email. Click the link to verify your withdrawal.
                            If you didn't receive it, click "Resend Link".
                          </p>
                        </div>

                        {/* Action Buttons */}
                        <div className="flex flex-wrap gap-2 sm:gap-3">
                          <button
                            onClick={() => handleResendVerification(withdrawal.id)}
                            disabled={resendingVerification === withdrawal.id}
                            className={`flex-1 min-w-[100px] sm:min-w-[140px] py-2 px-3 sm:px-4 rounded-lg font-medium transition-colors text-sm sm:text-sm ${
                              resendingVerification === withdrawal.id
                                ? 'bg-gray-600 text-gray-400 cursor-not-allowed'
                                : 'bg-blue-600 hover:bg-blue-700 text-white'
                            }`}
                          >
                            {resendingVerification === withdrawal.id ? (
                              <span className="flex items-center justify-center">
                                <div className="animate-spin rounded-full h-3 w-3 sm:h-4 sm:w-4 border-b-2 border-white mr-2"></div>
                                Sending...
                              </span>
                            ) : (
                              '📧 Resend Link'
                            )}
                          </button>
                          <button
                            onClick={() => handleCancelPendingWithdrawal(withdrawal.id)}
                            disabled={cancellingPendingId === withdrawal.id}
                            className={`flex-1 min-w-[100px] sm:min-w-[140px] py-2 px-3 sm:px-4 rounded-lg font-medium transition-colors text-sm sm:text-sm ${
                              cancellingPendingId === withdrawal.id
                                ? 'bg-gray-600 text-gray-400 cursor-not-allowed'
                                : 'bg-red-600 hover:bg-red-700 text-white'
                            }`}
                          >
                            {cancellingPendingId === withdrawal.id ? (
                              <span className="flex items-center justify-center">
                                <div className="animate-spin rounded-full h-3 w-3 sm:h-4 sm:w-4 border-b-2 border-white mr-2"></div>
                                Cancelling...
                              </span>
                            ) : (
                              '❌ Cancel'
                            )}
                          </button>
                        </div>
                      </div>
                    ))}
                </div>
              </div>
            )}

            {/* Withdrawal Availability */}
            <div className="bg-gray-700 rounded-lg p-3 sm:p-4 mb-4 sm:mb-6">
              <h4 className="text-sm sm:text-sm font-medium text-gray-300 mb-1">Available to withdraw now</h4>
              <p className="text-xl sm:text-2xl font-bold text-green-400">
                ${formatFlooredUsd(maxWithdrawalAmount)}
              </p>
              <p className="text-sm sm:text-sm text-white">Uses live backend withdrawal max</p>
              <div className="mt-2 space-y-1 text-xs text-gray-300">
                <div>Raw USDT commission balance: ${clientDisplayBalance.toFixed(2)}</div>
                <div>Escrowed / pending amount: ${escrowedWithdrawalAmount.toFixed(2)}</div>
                <div>Converted to shares: ${convertedToSharesAmount.toFixed(2)}</div>
                <div>Verified source-of-funds room: ${sourceOfFundsRoom.toFixed(2)}</div>
                <div>Network fee: ${formatFlooredUsd(withdrawalNetworkFee)}</div>
              </div>
            </div>

            {/* Locked Wallet Manager */}
            <LockedWalletManager userId={userId} onWalletsLoaded={handleLockedWalletsLoaded} />

            {/* Warning when user has locked wallets but none for selected network */}
            {lockedWallets.length > 0 && !walletLocked && (
              <div className="bg-yellow-900/30 border border-yellow-500/40 rounded-lg p-4 mb-4">
                <p className="text-yellow-300 text-sm">⚠️ You have registered wallet(s) but none for the selected network. Please select a network where you have a registered wallet, or register a new wallet above.</p>
              </div>
            )}

            {/* Withdrawal Form */}
            {!withdrawalsEnabled && (
              <div className="bg-yellow-900/30 border border-yellow-500/40 rounded-lg p-4 mb-4">
                <div className="flex items-center space-x-3">
                  <span className="text-2xl">⚠️</span>
                  <div>
                    <h4 className="font-semibold text-yellow-300">Withdrawals Temporarily Disabled</h4>
                    <p className="text-yellow-200 text-sm mt-1">
                      Withdrawals are temporarily disabled by the administrator. Please try again later.
                    </p>
                  </div>
                </div>
              </div>
            )}
            {withdrawalsEnabled && kycValidation?.canWithdraw && (
              <form onSubmit={handleWithdrawalSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm sm:text-sm font-medium text-gray-300 mb-2">
                    Withdrawal Amount (USD)
                  </label>
                  <div className="flex gap-2">
                    <input
                      type="number"
                      step="0.01"
                      min="5"
                      max={maxWithdrawalAmount}
                      value={withdrawalForm.amount}
                      onChange={(e) => setSafeWithdrawalAmount(e.target.value)}
                      className="flex-1 px-3 sm:px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-yellow-500 text-sm sm:text-base"
                      placeholder="Min $5.00"
                    />
                    <button
                      type="button"
                      onClick={() => {
                        const maxWithdrawable = maxWithdrawalAmount;
                        const recommended = floorToCents(Math.min(maxWithdrawable, maxWithdrawable * 0.8));
                        if (recommended >= 5) {
                          setWithdrawalForm(prev => ({ ...prev, amount: formatFlooredUsd(recommended) }));
                        } else if (maxWithdrawable >= 5) {
                          setWithdrawalForm(prev => ({ ...prev, amount: '5' }));
                        }
                      }}
                      disabled={maxWithdrawalAmount < 5}
                      className="px-3 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white text-sm sm:text-sm rounded-lg transition-colors whitespace-nowrap"
                      title="Set recommended amount (80% of max)"
                    >
                      Recommended
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        const maxWithdrawable = maxWithdrawalAmount;
                        if (maxWithdrawable >= 5) {
                          setWithdrawalForm(prev => ({ ...prev, amount: formatFlooredUsd(maxWithdrawable) }));
                        }
                      }}
                      disabled={maxWithdrawalAmount < 5}
                      className="px-3 py-2 bg-yellow-600 hover:bg-yellow-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-black font-semibold text-sm sm:text-sm rounded-lg transition-colors"
                      title="Set maximum amount"
                    >
                      Max
                    </button>
                  </div>
                  <p className="text-xs text-amber-300 mt-2">
                    Maximum available withdrawal amount is ${formatFlooredUsd(maxWithdrawalAmount)}.
                  </p>
                  {/* Fee Breakdown */}
                  <div className="mt-2 bg-blue-900/30 border border-blue-500/30 rounded-lg p-3">
                    <div className="flex items-center space-x-2 text-blue-300 text-sm sm:text-sm">
                      <span>ℹ️</span>
                      <span>A <strong>${formatFlooredUsd(withdrawalNetworkFee)} network fee</strong> applies to all withdrawals</span>
                    </div>
                    {withdrawalForm.amount && parseFloat(withdrawalForm.amount) > 0 && (
                      <div className="mt-2 pt-2 border-t border-blue-500/30 text-sm sm:text-sm" translate="no">
                        <div className="flex justify-between text-gray-300">
                          <span>You Will Receive:</span>
                          <span translate="no">${parseFloat(withdrawalForm.amount).toFixed(2)}</span>
                        </div>
                        <div className="flex justify-between text-gray-300">
                          <span>Network Fee:</span>
                          <span translate="no">+${formatFlooredUsd(withdrawalNetworkFee)}</span>
                        </div>
                        <div className="flex justify-between text-yellow-400 font-semibold mt-1 pt-1 border-t border-blue-500/20">
                          <span>Total Deducted from Balance:</span>
                          <span translate="no">${(parseFloat(withdrawalForm.amount) + 2).toFixed(2)}</span>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                <div>
                  <label className="block text-sm sm:text-sm font-medium text-gray-300 mb-2">
                    Wallet Address {walletLocked && <span className="text-green-400 text-xs ml-1">🔒 Locked</span>}
                  </label>
                  {walletLocked ? (
                    <div className="w-full px-3 sm:px-4 py-3 bg-gray-600 border border-green-600/50 rounded-lg text-white font-mono text-sm select-none">
                      {withdrawalForm.walletAddress}
                    </div>
                  ) : (
                    <div className="w-full px-3 sm:px-4 py-3 bg-red-900/30 border border-red-500/40 rounded-lg text-red-300 text-sm">
                      🔒 You must register a BSC withdrawal wallet before you can withdraw. Use the "Register BSC Wallet" section above.
                    </div>
                  )}
                  {walletLocked && (
                    <div className="mt-2">
                      <p className="text-green-400 text-xs">🔒 This address is locked to your account and cannot be changed.</p>
                      {onNavigateToSupport && (
                        <div className="mt-2">
                          <button
                            type="button"
                            onClick={onNavigateToSupport}
                            className="text-xs bg-gray-600 hover:bg-gray-500 text-yellow-300 px-3 py-1.5 rounded transition-colors"
                          >
                            📩 Request Wallet Address Change
                          </button>
                          <p className="text-gray-500 text-xs mt-1">To change your locked wallet address, submit a support ticket. An administrator will verify your identity before updating your wallet.</p>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                <div>
                  <label className="block text-sm sm:text-sm font-medium text-gray-300 mb-2">
                    Network
                  </label>
                  <div className="w-full px-3 sm:px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white text-sm sm:text-base">
                    BSC (BNB Smart Chain) — USDT BEP-20
                  </div>
                  <p className="text-blue-300 text-xs mt-1">ℹ️ BSC is the only supported withdrawal network. Other networks have been removed due to high transaction fees and because the Aureus Smart Contract only supports BSC withdrawals.</p>
                </div>

                {/* Error Message - displayed above the button */}
                {operationError && (
                  <div className="bg-red-900/20 border border-red-500/30 rounded-lg p-3">
                    <div className="flex items-center space-x-2">
                      <div className="text-xl">❌</div>
                      <p className="text-red-300 text-sm">{operationError}</p>
                    </div>
                  </div>
                )}

                {/* SECURITY (v4.0.52): Disabled unless wallet is locked */}
                <button
                  type="submit"
	                  disabled={withdrawalLoading || !withdrawalForm.amount || !walletLocked || withdrawalAmountExceedsMax}
                  className="w-full bg-yellow-600 hover:bg-yellow-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-black font-bold py-3 px-4 rounded-lg transition-colors text-sm sm:text-base"
                >
                  {withdrawalLoading ? (
                    <div className="flex items-center justify-center">
                      <div className="animate-spin rounded-full h-4 w-4 sm:h-5 sm:w-5 border-b-2 border-black mr-2"></div>
                      <span className="text-sm sm:text-sm">Submitting Request...</span>
                    </div>
                  ) : !walletLocked ? (
                    '🔒 Register a Wallet First'
                  ) : (
                    'Request Withdrawal'
                  )}
                </button>
              </form>
            )}
          </div>
        )}

        {activeTab === 'history' && (
          <div className="space-y-4 sm:space-y-6">
            {/* Withdrawal History */}
            <div className="bg-gray-800 rounded-lg border border-gray-700 p-4 sm:p-6">
              <h3 className="text-lg sm:text-xl font-bold text-white mb-4">💸 Withdrawal History</h3>
              {affiliateData.withdrawalHistory.length > 0 ? (
                <div className="space-y-3">
                  {affiliateData.withdrawalHistory.map((withdrawal) => (
                    <div key={withdrawal.id} className="flex flex-col sm:flex-row sm:justify-between sm:items-center p-3 sm:p-4 bg-gray-700 rounded-lg gap-2 sm:gap-0">
                      <div className="flex-1 min-w-0">
                        <p className="text-white font-medium text-sm sm:text-base">
                          ${withdrawal.withdrawal_amount.toFixed(2)} {withdrawal.currency}
                        </p>
                        <p className="text-sm sm:text-sm text-white">
                          {new Date(withdrawal.created_at).toLocaleDateString()} • {withdrawal.network}
                        </p>
                        {withdrawal.wallet_address && (
                          <p className="text-sm text-gray-500 font-mono break-all sm:break-normal">
                            <span className="sm:hidden">{withdrawal.wallet_address.substring(0, 15)}...{withdrawal.wallet_address.substring(withdrawal.wallet_address.length - 10)}</span>
                            <span className="hidden sm:inline">{withdrawal.wallet_address.substring(0, 10)}...{withdrawal.wallet_address.substring(withdrawal.wallet_address.length - 6)}</span>
                          </p>
                        )}
                      </div>
                      <div className="text-right">
                        <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                          withdrawal.status === 'completed' ? 'bg-green-900/30 text-green-400' :
                          withdrawal.status === 'pending' ? 'bg-yellow-900/30 text-yellow-400' :
                          withdrawal.status === 'approved' ? 'bg-blue-900/30 text-blue-400' :
                          'bg-red-900/30 text-red-400'
                        }`}>
                          {withdrawal.status.toUpperCase()}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-white text-center py-8">No withdrawal requests yet</p>
              )}
            </div>

            {/* Conversion History */}
            <div className="bg-gray-800 rounded-lg border border-gray-700 p-6">
              <h3 className="text-xl font-bold text-white mb-4">🔄 Conversion History</h3>
              {affiliateData.conversionHistory.length > 0 ? (
                <div className="space-y-3">
                  {affiliateData.conversionHistory.map((conversion) => (
                    <div key={conversion.id} className="flex justify-between items-center p-4 bg-gray-700 rounded-lg">
                      <div>
                        <p className="text-white font-medium">USDT → Shares Conversion</p>
                        <p className="text-sm text-white">
                          {new Date(conversion.created_at).toLocaleDateString()}
                        </p>
                        <p className="text-sm text-gray-500">
                          {conversion.status === 'approved' ? 'Approved' :
                           conversion.status === 'rejected' ? `Rejected: ${conversion.rejection_reason || 'No reason provided'}` :
                           'Pending Review'}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="text-red-400 font-medium">
                          ${Math.abs(conversion.usdt_amount).toFixed(2)} USDT
                        </p>
                        <p className="text-green-400 text-sm">
                          +{getConversionShares(conversion).toFixed(2)} Shares
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-white text-center py-8">No conversions yet</p>
              )}
            </div>

            {/* Transfer History */}
            <div className="bg-gray-800 rounded-lg border border-gray-700 p-6">
              <h3 className="text-xl font-bold text-white mb-4">📤 Transfer History</h3>
              {affiliateData.transferHistory.length > 0 ? (
                <div className="space-y-3">
                  {affiliateData.transferHistory.map((transfer) => (
                    <div key={transfer.id} className="flex justify-between items-center p-4 bg-gray-700 rounded-lg">
                      <div>
                        <p className="text-white font-medium">Share Transfer</p>
                        <p className="text-sm text-white">
                          {new Date(transfer.created_at).toLocaleDateString()}
                        </p>
                        <p className="text-sm text-gray-500">
                          To: User ID {transfer.recipient_user_id}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="text-yellow-400 font-medium">
                          -{transfer.shares_transferred} Shares
                        </p>
                        <span className={`px-2 py-1 rounded text-sm ${
                          transfer.status === 'completed' ? 'bg-green-900/30 text-green-400' :
                          'bg-yellow-900/30 text-yellow-400'
                        }`}>
                          {transfer.status.toUpperCase()}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-white text-center py-8">No transfers yet</p>
              )}
            </div>
          </div>
        )}

        {activeTab === 'notifications' && (
          <NotificationCenter
            userId={userId}
            className="bg-gray-800 rounded-lg border border-gray-700 p-6"
          />
        )}
      </div>

      {/* Non-dismissible Email Verification Modal — rendered at component root to prevent tab/list refresh from unmounting it */}
      {withdrawalVerificationModal && (
        <WithdrawalEmailVerificationModal
          confirmationData={withdrawalVerificationModal}
          userEmail={affiliateData?.user?.email || ''}
          onClose={() => setWithdrawalVerificationModal(null)}
          onDataRefresh={handleDataRefresh}
        />
      )}
    </div>
  );
};
